import { Matrix, Vector, Index, isvector, vector, matrix, ismatrix } from 'mdatools/arrays';
import { mean, sd } from 'mdatools/stat';
import { qf, qchisq } from 'mdatools/distributions';

import { pca_predict, pcamodel_get, pcares_get, pca_getreslength, pca_getmodellength }
   from '@mdatools-apps/wasm/pca.js';
import { pls_predict, plsres_get, pls_getreslength, pls_getmodellength }
   from '@mdatools-apps/wasm/pls.js';
import { gls_predict, glsres_get, gls_getreslength, gls_getmodellength }
   from '@mdatools-apps/wasm/gls.js';
import { da_getmodellength } from '@mdatools-apps/wasm/da.js';
import { checkModelBuffer } from '@mdatools-apps/wasm/utils.js';
import { prep_apply } from '@mdatools-apps/wasm/prep.js';


// constant values (colors, etc)
export const LINE_COLORS = {'cal': '#2679B2', 'pv': '#D22C2F', 'target': '#2679B2', 'alt': '#2679B2'};
export const FACE_COLORS = {'cal': '#f0f8ff', 'pv': '#fff8f0', 'target': '#f0f8ff', 'alt': '#f0f8ff'};
export const LABELS_COLOR = '#a0a0a0';
export const LABELS_COLOR_SELECTED = '#909090';


/**
 * Creates array with a sequence of values from `start` to `end`.
 * @param {number} start first value of sequence.
 * @param {number} end last value of sequence.
 * @param {number} by increment.
 * @returns {Array} array with sequence.
 */
export function seq(start, end, by = 1) {
   // the count of steps of 'by' which fit between the two ends, plus the start
   const length = Math.floor((end - start) / by) + 1;
   const out = Array(length);
   for (let i = 0; i < length; i++) {
      out[i] = start + i * by;
   }
   return out;
}


/**
 * Returns the elements of 'arr' whose key ('keys[i]', or 'i' without keys) is not in 'excluded',
 * plus the first 'nresp'. 'excluded' is read once into a Set: 'includes' on a Svelte state proxy
 * for every column made the filters of one fit take over 20 s on 14,500 columns, 13,333 excluded.
 */
export function withoutExcluded(arr, excluded, keys = null, nresp = 0) {
   const set = new Set(excluded);
   return arr.filter((v, i) => i < nresp || !set.has(keys ? keys[i] : i));
}


/**
 * Checks the list of excluded objects carried by a RESULT, and returns it as a dense array.
 *
 * A result's excluded-objects list is used to select rows without ever passing through a wrapper.
 * Each exporter reads it off the result and then decides row by row, with 'outliers.includes(i)',
 * whether to emit a value or a gap. Every entry therefore has to name a real row, and name it once.
 *
 * An entry that does not is silent rather than loud, which is why this throws. A fractional index,
 * an index past the end and a negative index all match no whole 'i', so the loop emits a row the
 * result has no values for and the file is written misaligned from there on. None of the exporters
 * catches that on its own: 'pca', 'nimca' and 'simca.js' test 'nAll - nOut' against 'res.nrows',
 * which compares how MANY rows were excluded and never WHICH, so the count is still right in all
 * three cases; 'pls' and 'pls1da' have no such test and index the result with a stride they derive
 * as 'nAll - nOut'; 'gls' computes that number, never uses it, and walks the dimensions of
 * 'res.Yp' instead, so it ends with cells missing rather than values shifted.
 *
 * A repeated index is refused for the same reason. It leaves the derived stride one too small in
 * 'pls' and 'pls1da'; in the other three it happens to be caught, or merely misreports a count in
 * the header, but the list is damaged either way.
 *
 * A falsy list is treated as an empty one, which is what every exporter did with it before.
 *
 * @param {Array} outliers - list of excluded object indices taken from a result.
 * @param {number} nrowsAll - number of rows in the dataset the EXPORTER was given, which is the
 * coordinate space these indices are in. That is not always the data the result was computed
 * from: 'nimca' predicts from the preprocessed 'calsetp' and exports against the original
 * 'calset', and the indices belong to the latter.
 * @returns {Array} a dense copy of the list, or an empty array.
 */
export function checkedResultOutliers(outliers, nrowsAll) {

   if (!outliers) return [];

   // the type is tested before the copy is taken: 'Array.from' erases the container, so a Set or
   // an array-like would come back as an ordinary array and be honoured as a list of objects
   if (!Array.isArray(outliers)) {
      throw new Error('Results can not be exported: the list of excluded objects is damaged.');
   }

   // '.every' SKIPS holes, so the copy has to come first - a hole would otherwise pass every test
   // below and then behave exactly like an index naming no row
   const values = Array.from(outliers);

   if (!values.every(Number.isInteger)) {
      throw new Error(
         'Results can not be exported: the list of excluded objects holds a value which is not ' +
         'a whole number.'
      );
   }

   if (!values.every(v => v >= 0 && v < nrowsAll)) {
      throw new Error(
         'Results can not be exported: the list of excluded objects names an object outside the ' +
         'dataset, which has ' + nrowsAll + ' of them.'
      );
   }

   if (new Set(values).size !== values.length) {
      throw new Error(
         'Results can not be exported: the list of excluded objects names the same object twice.'
      );
   }

   return values;
}


/**
 * Checks a dataset's list of original row numbers and gives the same array back.
 *
 * 'objindices' says which row of the loaded file each object came from. A restored session brings
 * it back verbatim and no 'checkSession' looks inside it - measured, none of the eight mentions it
 * - so it arrives unchecked wherever it is used, and every caller here copies it through rather
 * than rebuilding it from row positions.
 *
 * THE ARRAY THAT COMES BACK IS THE ONE THAT WENT IN, not the validated copy, and that is
 * load-bearing. 'handleOutliers' returns 'dataset.objindices' itself on its empty-outlier branch
 * and the result chains share that identity - 'r.objindices' becomes 'pvset.objindices' becomes
 * the next result's. The copy exists only because '.every' SKIPS holes, and it is a TRAILING hole
 * that needs it: an interior one is refused either way, because the entry after it compares
 * against 'undefined' and the rising test fails. A hole at the END is skipped by every test below
 * and would then behave exactly like a value naming no row.
 *
 * The values are positions in the ORIGINAL file, NOT positions in the values block, so they must
 * NOT be bounded by 'nrows'. The PCV set is the proof: all three PCVCalculators build 'pvset' with
 * 'objindices: calres.objindices' - the PRUNED list of original positions - while 'pvset.values'
 * holds only the kept rows. Exclude rows 0 and 1 of a six-row set and the list is [2, 3, 4, 5]
 * against four rows. Bounding by 'nrows' would refuse that honest session.
 *
 * So an oversized value is NOT refused here. Two places downstream subset with this list, through
 * a 'subset' which throws on an index past the end of the matrix, and NEITHER is a reliable net.
 * 'app_pls_predict' reaches its 'subset' only with a non-empty 'outliers' AND a dataset carrying
 * responses: with 'nresp' 0 it sets 'Yref' to null and refuses nothing. 'excludeOutliers' subsets
 * with the list 'handleOutliers' gave BACK, so an oversized value which is itself one of the rows
 * being removed is dropped before the subset sees it.
 *
 * A third consumer indexes with the list and checks NOTHING deliberately, though it does not let
 * an oversized value through either. 'getColorGroups' colours points by a variable through the
 * local 'subset' in 'shared/plots/Defaults.js', which writes 'x[samples[i]]' into a typed array,
 * so an oversized index stores 'undefined' and lands as NaN. Measured from there: the NaN poisons
 * the min and max 'split' computes, 'getAxisTicks' rejects the limits, and the branch dies with
 * 'TypeError: Cannot read properties of undefined'. Loud, but naming nothing - and it only bites
 * once the list has been pruned, since the helper returns 'x' untouched while the two lengths
 * match. The remaining consumers do just attach the list to a result, and the PCA and GLS paths
 * are all of that kind. There the cost is a wrong ORIGINAL ROW NUMBER shown in a plot or written
 * to a file, with the values beside it still lined up, plus a loud refusal later if the user
 * clicks that point, because the outlier list IS bounded.
 *
 * What the checks below do refuse are the shapes that MOVE data. 'app_pls_predict' maps this list
 * with 'v + 1' before handing it to 'subset', and that arithmetic runs on whatever was stored.
 * Measured on a four-row set, counting rows from 0 as this list does: a stored 2.5 selects row 2,
 * 'null' and 'false' both select row 0, and 'true' selects row 1 - every one of them silently, and
 * none of them the row that was meant. Only 'undefined' and NaN throw. A stored string throws only
 * while the dataset is small, because '2' + 1 is the string '21': from 21 rows up it quietly
 * selects row 20. A repeat or a swapped pair moves data the same way, which is what the rising
 * test refuses.
 *
 * @param {Array} objindices - the list to check, positions in the ORIGINAL file counted from 0.
 * @param {number} nrows - how many objects the values block beside it holds.
 * @returns {Array} 'objindices' itself, so a caller can attach the return value without changing
 * the identity the result chains rely on.
 */
export function checkedRowNumbers(objindices, nrows) {

   if (!Array.isArray(objindices)) {
      throw new Error('The dataset is damaged: its list of original row numbers is not a list.');
   }

   const rowNumbers = Array.from(objindices);

   if (rowNumbers.length !== nrows) {
      throw new Error(
         'The dataset is damaged: it has ' + nrows + ' objects but ' +
         rowNumbers.length + ' original row numbers.'
      );
   }

   if (!rowNumbers.every(Number.isInteger)) {
      throw new Error(
         'The dataset is damaged: one of its original row numbers is not a whole number.'
      );
   }

   if (!rowNumbers.every(v => v >= 0)) {
      throw new Error(
         'The dataset is damaged: one of its original row numbers is before the start of the file.'
      );
   }

   // strictly increasing, which also refuses a repeat. every producer builds this list from a loop
   // counter, from 'seq', or by pruning such a list in order, so it always rises; a pair that does
   // not is a list whose rows no longer line up with the values beside them
   if (!rowNumbers.every((v, i) => i === 0 || v > rowNumbers[i - 1])) {
      throw new Error(
         'The dataset is damaged: its original row numbers are out of order or name the same ' +
         'row twice.'
      );
   }

   return objindices;
}


/**
 * Checks a model's list of variable numbers against the calibration block it was fitted from.
 *
 * 'varindices' says which variable of the CALIBRATION BLOCK each column of a model stands for -
 * of the block and not of the loaded file, which stop being the same thing the moment
 * preprocessing trims: 'prep_trim' renumbers the columns it keeps from 0, so trimming five off
 * the left leaves the file's variable 5 numbered 0. The first 'nresp' entries number the responses
 * 0, 1, 2 ..., and the rest number the predictors that survived exclusion, each keeping the number
 * it had in that block. A restored session brings the whole model back verbatim and no
 * 'checkSession' looks inside it - and a session arrives from a FILE as well as from IndexedDB
 * ('loadSession'), so this is something a user can be handed rather than only something a damaged
 * database can produce.
 *
 * WHY THIS IS REBUILT RATHER THAN TESTED. The one place a damaged list moves data is the
 * 'excludeVars' handler in the pls and pls1da training tabs: it turns the positions a user marked
 * on the Variable importance plot into variable numbers, and those numbers are what the refit
 * EXCLUDES. Testing the list's shape is not enough there. With one response, four predictors and
 * variable 1 already excluded, the honest list is [0, 0, 2, 3]; damage it to [0, 0, 1, 2] and it is
 * still the right length, still whole, still non-negative, still rising, still carrying the right
 * response prefix - and marking the last predictor now excludes variable 2 instead of 3, silently.
 * So the list is rebuilt from what the model was fitted with and compared, position by position.
 *
 * WHY THE WIDTH IS AN ARGUMENT. Deriving the predictor universe from the model's own numbers is
 * circular: 'npred + exclvars.length' grows with a damaged list, so an exclusion list extended from
 * [] to [3] widens the universe to fit itself and validates. 'npredTotal' must come from the
 * calibration block the caller fitted from - 's.calsetp' - which no part of the model can move.
 *
 * WHY BOTH EXCLUSION LISTS. There are THREE lists in play, not two. The handler reads the model's
 * 'exclvars' but appends its result to the session's own 's.exclvars', and it is the session list
 * that reaches the refit. An honest model beside a damaged session list passes any check on the
 * model alone and then excludes the wrong accumulated set, so the two are required to agree.
 * Contents and order, not identity: the two ARE the same array in a session that has not been
 * reloaded, since 'pls_fit' hands the caller's own array back on the model, and a reload leaves
 * separate arrays holding the same values in the same order. Nothing here can tell an order-strict
 * comparison from a set-equality one, because no honest state distinguishes them; order-strict is
 * simply the stronger of two sound choices.
 *
 * The exclusion list is NOT sorted and must not be treated as if it were. It accumulates by
 * 'concat' in the order the user clicked, so excluding variable 3 and then variable 1 leaves the
 * honest [3, 1]. Membership is tested with a Set for that reason.
 *
 * What this still cannot refuse is a session whose 'varindices', model 'exclvars' and session
 * 'exclvars' were ALL rewritten together to describe a different exclusion set: the three then
 * agree with each other, which is the whole of what this function can see. That is consistency
 * among these three inputs and nothing wider - 'xvarindices', 'xvarlabels' and 'xvarvalues' are
 * stored separately on the model and are what the Variable importance plot actually draws, so the
 * display need not agree with the damage.
 *
 * @param {Array} varindices - the model's list, responses first and then surviving predictors.
 * @param {number} nresp - how many responses the model was fitted with.
 * @param {number} npredTotal - predictors in the calibration block, BEFORE any exclusion.
 * @param {Array} modelExclvars - the list the model records having been fitted without.
 * @param {Array} sessionExclvars - the list the session will hand to the next fit.
 * @returns {Array} the caller's own list, unchanged - nothing here rewrites it, and nothing
 * attaches it to a result either: its one caller maps it and drops it.
 */
export function checkedVarIndices(varindices, nresp, npredTotal, modelExclvars, sessionExclvars) {

   if (!Array.isArray(varindices)) {
      throw new Error('The model is damaged: its list of variable numbers is not a list.');
   }

   if (!Array.isArray(modelExclvars) || !Array.isArray(sessionExclvars)) {
      throw new Error('The model is damaged: its list of excluded variables is not a list.');
   }

   // the model's list is copied dense before anything is tested, and it is the ONLY one of the
   // three that needs to be. '.every' SKIPS holes, so a hole in this list would be skipped by both
   // the agreement test and the range test below, while 'new Set' visits it and carries an
   // 'undefined' that excludes no variable: a model list damaged from [0, 1] to [<hole>, 1], beside
   // an honest session list [0, 1], validates against an honest 'varindices' and lets the two lists
   // disagree - which is the whole defect. Measured, not assumed.
   //
   // the other two are only ever read by index and by '.length', and a hole reads as 'undefined'
   // either way there, so copying them would be code no test could exercise. 'varindices' in
   // particular cannot smuggle a hole past the reconstruction below: that walks the rebuilt list,
   // which is dense, and reads 'varindices[i]' at every position
   const modelExcluded = Array.from(modelExclvars);

   if (modelExcluded.length !== sessionExclvars.length ||
       !modelExcluded.every((v, i) => v === sessionExclvars[i])) {
      throw new Error(
         'The session is damaged: the variables it has excluded are not the ones the model was ' +
         'built without.'
      );
   }

   const excludedSet = new Set(modelExcluded);
   if (excludedSet.size !== modelExcluded.length ||
       !modelExcluded.every(v => Number.isInteger(v) && v >= 0 && v < npredTotal)) {
      throw new Error(
         'The model is damaged: one of its excluded variables is not a whole number naming a ' +
         'variable of the training set, or it names the same one twice.'
      );
   }

   // the shape every producer emits, rebuilt: the responses in order, then the predictors that
   // survived, each keeping its own number
   const expected = [];
   for (let i = 0; i < nresp; i++) expected.push(i);
   for (let i = 0; i < npredTotal; i++) {
      if (!excludedSet.has(i)) expected.push(i);
   }

   if (varindices.length !== expected.length) {
      throw new Error(
         'The model is damaged: it describes ' + expected.length + ' variables but carries ' +
         varindices.length + ' variable numbers.'
      );
   }

   const wrong = expected.findIndex((v, i) => v !== varindices[i]);
   if (wrong >= 0) {
      throw new Error(
         'The model is damaged: variable number ' + (wrong + 1) + ' should be ' + expected[wrong] +
         ' but is ' + varindices[wrong] + '.'
      );
   }

   return varindices;
}


/**
 * Turns positions marked on the Variable importance plot into the variable numbers to exclude.
 *
 * The pls and pls1da training tabs both do exactly this and differ only in where the PLS model
 * sits on the session ('s.model' against 's.model.pls'), so the two copies are one function here.
 * That is not only tidiness. The arguments 'checkedVarIndices' needs were otherwise spelled out at
 * each call site, and handing it the MODEL's exclusion list where the session's belongs would turn
 * its agreement test into a tautology and reopen the defect silently, with nothing under
 * 'shared/test' able to see it. There is one exclusion list to hand over now, and this file is
 * where it is handed over.
 *
 * 'pos' is not checked here. A position past the end of the model's list maps to 'undefined',
 * which survives into the session's list and is refused by 'checkedIndexCopy' at the next fit -
 * the behaviour this function was factored out of, carried over unchanged.
 *
 * @param {Array} pos - positions marked on the plot, counted from 0 over the model's predictors.
 * @param {Object} session - the training tab's session slice, holding 'calsetp' and 'exclvars'.
 * @param {Object} model - the PLS model those positions were marked on.
 * @returns {Array} the variable numbers to add to the session's list of excluded variables.
 */
export function excludedVarNumbers(pos, session, model) {

   // the predictor count comes from 'calsetp', the calibration block this model was fitted from,
   // and never from the model itself - see 'checkedVarIndices' for why that would be circular.
   // 'calsetp' carries every predictor: exclusion happens inside 'pls_fit', which is handed the
   // list separately, and 'prep_trim' renumbers what it trims, so the segment is always dense
   const varindices = checkedVarIndices(model.varindices, model.nresp,
      session.calsetp.values.ncols - session.calsetp.nresp, model.exclvars, session.exclvars);

   // 'model.nresp' is added because 'varindices' numbers the responses first and the plot the
   // positions came from shows predictors only
   return pos.map(i => varindices[i + model.nresp]);
}


/**
 * Creates arrays with object indices, labels, classes and classNames without outliers.
 * @param {Object} dataset — JSON with dataset object.
 * @param {Array} outliers — array with object indices removed as outliers.
 * @returns {Array} array with four arrays — object indices, labels, classes and unique class names.
 */
export function handleOutliers(dataset, outliers) {

   // this is exported and has nothing behind it, so it cannot lean on a wrapper having checked
   // the list. rows are matched with 'outlierSet.has(i)' where 'i' is a whole number, so anything
   // that is not one matches no row at all and the row it names stays in the result - silently.
   // an index past the end of the dataset does exactly the same, which is why the range is
   // tested here as well. the wrappers have always refused that half through 'isArrCorrect';
   // the non-whole half is refused as of this change.
   //
   // the container is tested first and the copy taken second: 'Array.from' erases the container,
   // so a Set or an array-like would come back as an ordinary array and be accepted. the copy is
   // then dense - '.every' SKIPS holes, and a hole matches no row either
   if (!Array.isArray(outliers)) {
      throw new Error('handleOutliers: indices in "outliers" array must be whole numbers.');
   }

   const outlierValues = Array.from(outliers);
   if (!outlierValues.every(Number.isInteger)) {
      throw new Error('handleOutliers: indices in "outliers" array must be whole numbers.');
   }

   if (!outlierValues.every(v => v >= 0 && v < dataset.values.nrows)) {
      throw new Error('handleOutliers: some indices in "outliers" array are outside the expected range.');
   }

   // the list of original row numbers is checked here for the same reason the outlier list above
   // is: this function copies it through rather than rebuilding it, so a damaged one would reach
   // the result unchanged. the checks live in 'checkedRowNumbers' because two more callers reach
   // a result WITHOUT coming through here - see that function.
   //
   // 'values.nrows' is read only once the container is confirmed, which is the order this function
   // had before the checks moved out. with an EMPTY outlier list nothing above touches
   // 'dataset.values' - '.every' on an empty array never runs its callback - so a dataset damaged
   // in both places at once, no values block and no list, has to report the list rather than die
   // reading 'nrows' off null. the 0 is never used: the container is what 'checkedRowNumbers'
   // refuses first
   const nrows = Array.isArray(dataset.objindices) ? dataset.values.nrows : 0;
   checkedRowNumbers(dataset.objindices, nrows);

   let indices, classes, labels, classNames = [];
   if (outlierValues.length > 0) {
      // filter the outlier out
      const nAll = dataset.values.nrows;
      const sampleIndices = dataset.objindices;
      const n = nAll - outlierValues.length;
      const outlierSet = new Set(outlierValues);

      // a dataset without groups has to stay without groups: reading 'classes[i]' from it gives
      // undefined for every row, which would otherwise be collected into a list of one group
      // named 'undefined' and shown as such in the legend
      const hasClasses = dataset.classes && dataset.classes.length > 0;

      indices = Array(n);
      classes = hasClasses ? Array(n) : [];
      labels = Array(n);
      let j = 0;
      for (let i = 0; i < nAll; i++) {
         if (outlierSet.has(i)) continue;
         indices[j] = sampleIndices[i];
         labels[j] = dataset.objlabels[i]
         if (hasClasses) {
            classes[j] = dataset.classes[i];
            if (classNames.length === 0 || !classNames.includes(classes[j])) {
               classNames.push(classes[j]);
            }
         }
         j = j + 1;
      }
   } else {
      indices = dataset.objindices;
      labels = dataset.objlabels;
      classes = dataset.classes;
      classNames = dataset.classNames;
   }

   return [indices, labels, classes, classNames];
}


/**
 * Flips the reference class label of every object named in `relabeled`.
 *
 * @param {Matrix} values - dataset values; column 1 holds the class label. Mutated in place.
 * @param {Array} relabeled - 0-based row numbers of the objects whose label must be flipped.
 * @param {number} nresp - number of response columns in `values`.
 *
 * @description
 * PLS1-DA stores the class as a single reference column of -1 and +1, and the user can flip an
 * object's class by hand. The three places which did that held a copy of the same loop; this is
 * the one copy they now share.
 *
 * Every check runs before the first write, so a list which is refused leaves `values` exactly as
 * it was rather than half flipped.
 *
 * The reason this validates at all: the loop writes `y[i]` on a `Float64Array`, and such a write
 * never raises however damaged the index is. A number which names no row - fractional, negative
 * or past the end - is DISCARDED outright, while `true`, `null` and `undefined` add an ordinary
 * property and leave the data alone. Either way the object silently kept its original class while
 * the Relabeled widget still listed it as changed. A string which spells a row exactly is the one
 * exception - `y['3']` writes row 3 just as `y[3]` does, while `y['1.5']` and `y['03']` do not -
 * so it is refused here for the sake of a single rule rather than because it corrupts anything.
 *
 * Every refusal below but the type test is reachable. A session is restored verbatim, and
 * pls1da - the one app that passes a list - requires it to be an array in `checkSession` and
 * never looks at its entries, so a stored list can arrive holding a duplicate and a stored
 * dataset can arrive with a damaged `nresp`. Through the live interface the two producers do
 * refuse a duplicate with `.includes` and `addDummy` does give `nresp = 1`, but neither of them
 * stands between a restored session and this function.
 *
 * @returns {Matrix} the same `values`, with the named labels flipped.
 */
export function relabelObjects(values, relabeled, nresp) {

   if (!relabeled) return values;

   // the type is tested before the copy is taken: 'Array.from' erases the container, so a Set or
   // an array-like would come back as an ordinary array and be honoured as a list of objects
   if (!Array.isArray(relabeled)) {
      throw new Error('Objects can not be relabelled: the list of relabelled objects is damaged.');
   }

   // '.every' SKIPS holes, so the copy has to come first - a hole would otherwise pass every test
   // below, and 'for..of' then yields 'undefined' for it, which does not flip a row and instead
   // adds an ordinary property to the typed array
   const indices = Array.from(relabeled);

   if (indices.length === 0) return values;

   if (!indices.every(Number.isInteger)) {
      throw new Error(
         'Objects can not be relabelled: the list of relabelled objects holds a value which is ' +
         'not a whole number.'
      );
   }

   if (!indices.every(v => v >= 0 && v < values.nrows)) {
      throw new Error(
         'Objects can not be relabelled: the list of relabelled objects names an object outside ' +
         'the dataset, which has ' + values.nrows + ' of them.'
      );
   }

   // a repeated object is flipped twice and ends up back where it started, so the list would
   // claim a change which did not happen
   if (new Set(indices).size !== indices.length) {
      throw new Error(
         'Objects can not be relabelled: the list of relabelled objects names the same object twice.'
      );
   }

   // 'nresp < 1' on its own refuses none of NaN, undefined or the string '1' - each compares
   // false - and column 1 would then be flipped without it being established as the class column
   if (!Number.isInteger(nresp) || nresp < 1) {
      throw new Error('Objects can not be relabelled: the dataset has no column with classes.');
   }

   const y = values.getcolref(1);
   for (const i of indices) {
      y[i] = y[i] > 0 ? -1 : 1;
   }

   return values;
}


/**
 * Generates unique pseudo-hash number based on current time and date.
 * @returns  string with the hash.
 */
export function genhash() {
    const now = new Date();
    const uniqueId = `${now.getFullYear()}${(now.getMonth() + 1)
        .toString()
        .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now
        .getHours()
        .toString()
        .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now
        .getSeconds()
        .toString()
        .padStart(2, '0')}${now
        .getMilliseconds()
        .toString()
        .padStart(3, '0')}`;
    return uniqueId;
}


/**
 * Applies GLS model to a new dataset and adds additional attributes.
 *
 * @param {Promise} Module - WASM module (from Promise).
 * @param {Object} model - object with GLS model.
 * @param {Matrix} dataset - object with original dataset.
 * @param {Array} outliers - array with outliers.
 * @param {boolean} doPrep - apply or not preprocessing if preprocessing model is available.
 *
 * @returns {Object} object with PLS results
 */
export function app_gls_predict(Module, model, dataset, outliers, doPrep = false) {

   // get arrays with object indices and labels without outliers
   const [objindices, objlabels] = handleOutliers(dataset, outliers);

   // if preprocesssing available apply it first

   const v = doPrep && model.prep && model.prep.ml && model.prep.ml.length > 0  ?
      prep_apply(Module, dataset.values, dataset.nresp, model.prep, false, true) :
      dataset.values;

   // make predictions and add labels and indices
   const r = gls_predict(Module, model, v, dataset.nresp, outliers);
   withoutResiduals(r);

   r['hash'] = genhash();
   r['objindices'] = objindices;
   r['objlabels'] = objlabels;
   r['varlabels'] = model.varlabels;
   r['resplabels'] = model.yvarlabels;

   return r;
}


/**
 * Applies PLS model to a new dataset and adds additional attributes.
 * @param {Promise} Module - WASM module (from Promise).
 * @param {Object} model - object with PLS model.
 * @param {Matrix} dataset - object with original dataset.
 * @param {Array} outliers - array with outliers.
 * @param {boolean} doPrep - apply or not preprocessing if preprocessing model is available.
 * @param {Array} relabeled - array with relabeled objects (only needed for PLS1DA predictions).
 *
 * @description
 * If `relabeled` is provided it is assumed that predictions are made for PLS1-DA model and that
 * all reference y-values are either -1 and +1. In this case for all objects whose indices are
 * in this array the reference y-value will be swapped.
 *
 * @returns {Object} JSON with PLS results.
 */
export function app_pls_predict(Module, model, dataset, outliers, doPrep = false, relabeled = null) {

   // get arrays with object indices and labels without outliers
   const [objindices, objlabels] = handleOutliers(dataset, outliers);

   // get copy of the dataset values, if preprocesssing is available apply it first
   const v = doPrep && model.prep && model.prep.ml && model.prep.ml.length > 0  ?
      prep_apply(Module, dataset.values, dataset.nresp, model.prep, false, true) :
      dataset.values.copy();

   // if predictions are made for PLS1-DA and array with objects whose reference
   // y-values must be relabeled is provided, swap the y-values
   relabelObjects(v, relabeled, dataset.nresp);

   // make predictions and add labels and indices
   const r = pls_predict(Module, model, v, dataset.nresp, outliers);
   withoutResiduals(r);

   r['hash'] = genhash();

   // 'pls_predict' puts the list on the result by reference - it is the same array the caller
   // goes on pushing into as objects are marked, so the result would silently stop describing
   // what it was computed with. the copy pins it, as 'app_pca_predict' does below
   r['outliers'] = Array.isArray(outliers) ? [...outliers] : [];
   r['objindices'] = objindices;
   r['objlabels'] = objlabels;
   r['varlabels'] = model.varlabels;

   if (dataset.nresp > 0) {
      r['Yref'] = (outliers && outliers.length > 0) ?
         v.subset(objindices.map(v => v + 1), Index.seq(1, dataset.nresp)) :
         v.subset(null, Index.seq(1, dataset.nresp));
   } else {
      r['Yref'] = null;
   }

   return r;
}


/**
 * Applies PCA model to a new dataset and adds additional attributes.
 * @param {Promise} Module - WASM module (from Promise).
 * @param {Object} model - object with PCA model.
 * @param {Matrix} dataset - object with original dataset.
 * @param {Array} outliers - array with outliers.
 * @param {boolean} doPrep - apply or not preprocessing if preprocessing model is available.
 * @returns {Object} JSON with PCA results
 */
export function app_pca_predict(Module, model, dataset, outliers, doPrep = false) {

   // get arrays with sample indices, classes and object labels without outliers. the group names
   // are taken from here as well and not from the dataset: removing the last object of a group
   // has to remove the group with it, or it stays in the legend and, with confidence ellipses on,
   // is asked for a centre it has no points to compute
   const [objindices, objlabels, classes, classNames] = handleOutliers(dataset, outliers);

   // if preprocesssing available apply it first
   const v = doPrep && model.prep && model.prep.ml && model.prep.ml.length > 0  ?
      prep_apply(Module, dataset.values, 0, model.prep, false, true) :
      dataset.values;


   // make predictions and add labels and indices
   const r = pca_predict(Module, model, v, outliers);
   withoutResiduals(r);
   r['hash'] = genhash();

   // 'pca_predict' already puts the list on the result, but by reference - it is the same array
   // the caller goes on pushing into as objects are marked, so the result would silently stop
   // describing what it was computed with. the copy pins it. every consumer needs the list to line
   // the rows of 'v' up with the rows of the dataset, and taking it from somewhere else lets the
   // two disagree, which is how the pv results came to be exported with the outliers subtracted
   // twice
   r['outliers'] = Array.isArray(outliers) ? [...outliers] : [];
   r['objindices'] = objindices;
   r['objlabels'] = objlabels;
   r['classes'] = classes;
   r['classNames'] = classNames;
   // the model's list, as the pls and gls helpers take it: the loadings are the columns the
   // model was fitted on, and a set the loader did not relabel (an SPC file) keeps every column
   r['varlabels'] = model.varlabels;

   // model outcomes required for plots
   r['eigenvals'] = pcamodel_get(model, 'eigenvals');
   r['cNh'] = pcamodel_get(model, "cNh");
   r['cNq'] = pcamodel_get(model, "cNq");
   r['ch0'] = pcamodel_get(model, "ch0");
   r['cq0'] = pcamodel_get(model, "cq0");
   r['rNh'] = pcamodel_get(model, "rNh");
   r['rNq'] = pcamodel_get(model, "rNq");
   r['rh0'] = pcamodel_get(model, "rh0");
   r['rq0'] = pcamodel_get(model, "rq0");

   const n = model.nrows;
   r['T2lim'] =  (2 * (n - 1) / (n - 2)) * qf(0.95, 2, (n - 2));
   return r;
}


/**
 * Tells whether two arrays of scores agree: each element within 1e-8 times the largest finite
 * |value| of 'a', a NaN matching a NaN and an Inf the same Inf at the same place.
 * @param {Array} a - stored scores.
 * @param {Array} b - scores computed again.
 * @returns {boolean} true if they agree.
 */
export function scoresMatch(a, b) {
   if (a.length !== b.length) return false;
   let scale = 0;
   for (const x of a) if (Number.isFinite(x) && Math.abs(x) > scale) scale = Math.abs(x);
   for (let i = 0; i < a.length; i++) {
      if (a[i] === b[i] || (Number.isNaN(a[i]) && Number.isNaN(b[i]))) continue;
      if (!(Math.abs(a[i] - b[i]) <= 1e-8 * scale)) return false;
   }
   return true;
}


/**
 * Computes the x residuals E of a stored result again, from the data it was computed with.
 * @param {Object} Module - WASM module.
 * @param {Object} model - the model the result came from.
 * @param {Object} dataset - the set it was computed for.
 * @param {Object} result - the stored result.
 * @param {boolean} doPrep - apply the model's preprocessing first, as the predict helpers do.
 * @returns {Float64Array} E, rows by columns, as the result stored it.
 */
export function app_residuals(Module, model, dataset, result, doPrep = false) {
   // a PLS model is also a PCA one, so PLS and GLS are tested first
   const kind = model.class.includes('plsmodel') ? 'pls' :
      model.class.includes('glsmodel') ? 'gls' : 'pca';
   const nresp = kind === 'pca' ? 0 : dataset.nresp;
   const v = doPrep && model.prep && model.prep.ml && model.prep.ml.length > 0 ?
      prep_apply(Module, dataset.values, nresp, model.prep, false, true) : dataset.values;
   const outliers = result.outliers ?? [];
   const r = kind === 'pls' ? pls_predict(Module, model, v, dataset.nresp, outliers) :
      kind === 'gls' ? gls_predict(Module, model, v, dataset.nresp, outliers) :
      pca_predict(Module, model, v, outliers);
   const get = kind === 'pls' ? plsres_get : kind === 'gls' ? glsres_get : pcares_get;
   // a sanity check: it catches other rows, columns or scores, not a change the model ignores
   if (r.nrows !== result.nrows || r.npred !== result.npred ||
      !scoresMatch(get(result, 'T'), get(r, 'T'))) {
      throw new Error('app_residuals: the data do not give the stored result.');
   }
   return get(r, 'E');
}


// the length of 'r.v' with E, from the result's own sizes; null if a size is not a whole number
function fullLength(r) {
   const cls = Array.isArray(r.class) ? r.class : [];
   const sizes = cls.includes('plsres') || cls.includes('glsres') ?
      [r.nrows, r.nresp_model, r.ncomp, r.npred] :
      cls.includes('pcares') ? [r.nrows, r.ncomp, r.ncols] : null;
   if (!sizes || !sizes.every((x) => Number.isInteger(x) && x >= 0)) return null;
   return cls.includes('plsres') ? pls_getreslength(...sizes) :
      cls.includes('glsres') ? gls_getreslength(...sizes) : pca_getreslength(...sizes);
}


/**
 * Cuts the x residuals E, the last block of 'r.v', from a full PCA, PLS or GLS result. Every typed
 * array of the result on the old buffer moves to the new one, or is copied if it held E.
 * @param {Object} r - the result, changed in place.
 * @returns {boolean} true if E was cut, false if 'r.v' is not of the full length.
 */
export function withoutResiduals(r) {
   const full = fullLength(r);
   if (!(r.v instanceof Float64Array) || full === null || r.v.length !== full) return false;
   const old = r.v;
   const n = r.class.includes('plsres') || r.class.includes('glsres') ? r.npred : r.ncols;
   r.v = old.slice(0, old.length - r.nrows * n);
   const seen = new Set();
   const walk = (x) => {
      if (!x || typeof x !== 'object' || seen.has(x)) return;
      seen.add(x);
      for (const k of Object.keys(x)) {
         const a = x[k];
         if (!ArrayBuffer.isView(a)) { walk(a); continue; }
         if (a === r.v || a.buffer !== old.buffer) continue;
         const offset = a.byteOffset - old.byteOffset;
         x[k] = offset >= 0 && offset % a.BYTES_PER_ELEMENT === 0 &&
            offset + a.byteLength <= r.v.byteLength ?
            new a.constructor(r.v.buffer, offset, a.length) : a.slice();
      }
   };
   walk(r);
   return true;
}


/**
 * Cuts E from every full result in a session, as 'withoutResiduals' does.
 * @param {*} value - the session or a part of it, changed in place.
 * @returns {boolean} true if any result was cut.
 */
export function dropResiduals(value) {
   if (!value || typeof value !== 'object' || ismatrix(value) || isvector(value) ||
      ArrayBuffer.isView(value)) return false;
   if (Array.isArray(value.class) &&
      (value.class.includes('pcares') || value.class.includes('glsres'))) {
      return withoutResiduals(value);
   }
   let cut = false;
   if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) if (dropResiduals(value[i])) cut = true;
   } else {
      const keys = Object.keys(value);
      for (let i = 0; i < keys.length; i++) if (dropResiduals(value[keys[i]])) cut = true;
   }
   return cut;
}


/**
 * Tells whether every PCA, PLS and GLS result in a session has values its sizes fit, with or
 * without E, as 'withoutResiduals' leaves it. The getters cut 'r.v' at offsets taken from those
 * sizes, so other values give short or misaligned blocks, which nothing refuses. 'npred' of a
 * PLS or GLS result sizes E alone, so once E is cut it is checked only for being a whole number.
 * @param {*} value - the session or a part of it.
 * @returns {boolean} false if a result's tag is not an array, its sizes are not whole numbers or
 * its values not a Float64Array of their length, with or without E.
 */
export function resultsFit(value) {
   if (!value || typeof value !== 'object' || ismatrix(value) || isvector(value) ||
      ArrayBuffer.isView(value)) return true;

   // a result by the getters' own test, '.includes', which a string tag passes as well
   const cls = value.class;
   if ((Array.isArray(cls) || typeof cls === 'string') &&
      ['pcares', 'plsres', 'glsres'].some((t) => cls.includes(t))) {
      const full = fullLength(value);
      if (!Array.isArray(cls) || full === null || !(value.v instanceof Float64Array)) return false;
      const n = cls.includes('plsres') || cls.includes('glsres') ? value.npred : value.ncols;
      return value.v.length === full || value.v.length === full - value.nrows * n;
   }

   return (Array.isArray(value) ? value : Object.values(value)).every(resultsFit);
}


// the model tags with the header and the length 'checkModelBuffer' takes for them, as the predict
// wrappers pass them. 'plsmodel' comes before 'pcamodel', which a PLS model carries as well
const MODEL_LAYOUTS = [
   ['plsmodel', [[0, 'nresp'], [1, 'npred'], [2, 'ncomp']],
      (ny, nx, nc) => pls_getmodellength(nx, ny, nc)],
   ['glsmodel', [[0, 'nresp'], [1, 'npred'], [2, 'ncomp']],
      (ny, nx, nc) => gls_getmodellength(nx, ny, nc)],
   ['damodel', [[0, 'nrows'], [1, 'nresp'], [2, 'ncomp']],
      (nr, nc, ncomp) => da_getmodellength(nc, ncomp)],
   ['pcamodel', [[0, 'ncols'], [1, 'ncomp']], pca_getmodellength],
];

/**
 * Tells whether every PCA, PLS, GLS and DA model in a value has values its sizes fit: the type,
 * header and length check of 'checkModelBuffer', and for a PLS model 'coeffs', 'vip' and 'sr' as
 * Float64Arrays of 'npred * nresp * ncomp' values. The getters and plots cut these at offsets
 * taken from the sizes, and a session restored with its results is not predicted again.
 * @param {*} value - a session, a part of it or a parsed model file.
 * @returns {boolean} false if a model's tag is not an array or its values do not fit its sizes.
 */
export function modelsFit(value) {
   if (!value || typeof value !== 'object' || ismatrix(value) || isvector(value) ||
      ArrayBuffer.isView(value)) return true;

   // a model by the getters' own test, '.includes', which a string tag passes as well
   const cls = value.class;
   const layout = (Array.isArray(cls) || typeof cls === 'string') &&
      MODEL_LAYOUTS.find(([t]) => cls.includes(t));
   if (layout) {
      if (!Array.isArray(cls)) return false;
      try { checkModelBuffer(value, 'stored model', layout[1], layout[2]); } catch { return false; }
      if (layout[0] !== 'plsmodel') return true;
      const n = value.npred * value.nresp * value.ncomp;
      return ['coeffs', 'vip', 'sr'].every((f) => value[f] instanceof Float64Array &&
         value[f].length === n);
   }

   return (Array.isArray(value) ? value : Object.values(value)).every(modelsFit);
}


// a string which is a number and nothing else: a sign, digits with one decimal point, an exponent
const NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/**
 * Converts string or array of strings to number(s). A string which is not a number in full gives
 * NaN: 'Number.parseFloat' took the number a string starts with, so '1.234,5' in a file with a
 * decimal comma loaded as 1.234 and '5 mg' as 5.
 * @param {Array|string} str - string or array of strings should be converted to numbers.
 * @param {string} dec - character for decimal delimeter.
 * @returns {Array|Number} number or array of numbers.
 */
function str2num(str, dec = '.') {
   if (Array.isArray(str)) return str.map(v => str2num(v, dec));
   const v = dec === '.' ? str.trim() : str.trim().replace(dec, '.');
   return NUMBER.test(v) ? Number.parseFloat(v) : NaN;
}


/**
 * Returns true if at least one value in array is NaN.
 * @param {Array} arr - array with values.
 * @returns logical value.
 */
function hasNaN(arr) {
   return arr.some(v => Number.isNaN(v));
}


/**
 * Removes white spaces and double quotes in a string (e.g. '" or """)
 * @param {String} s - string value.
 * @returns {String}
 */
function clearLabels(s) {
   return s.replace('\t', '').trim().replace(/^(['"])(.*)\1$/, '$2');
}


/**
 * Splits a row of a CSV file into its fields. Inside a field in double quotes the separator is
 * text and a doubled quote stands for one quote, as 'csvRow' writes them; the quotes around the
 * field stay and come off where they came off before. A field not opened by a quote is taken as
 * it is. A row whose quote is never closed, or with text behind a closing quote, is split on the
 * separator alone.
 * @param {string} row - one row of the file.
 * @param {string} sep - symbol used for separation of values.
 * @returns {Array} array of strings, one per field.
 */
export function splitCSVRow(row, sep) {
   const fields = [];
   let field = '';
   let quoted = false;
   let closed = false;
   for (let i = 0; i < row.length; i++) {
      const c = row[i];
      if (quoted) {
         if (c !== '"') {
            field += c;
         } else if (row[i + 1] === '"') {
            field += '"';
            i++;
         } else {
            field += '"';
            quoted = false;
            closed = true;
         }
      } else if (c === sep) {
         fields.push(field);
         field = '';
         closed = false;
      } else if (closed) {
         // text behind a closing quote: not a quoted row, split as before the quotes were read
         return row.split(sep);
      } else if (c === '"' && field.trim() === '') {
         // a quote opens a quoted field only at the field's start, spaces before it stay
         field += '"';
         quoted = true;
      } else {
         field += c;
      }
   }
   if (quoted) return row.split(sep);
   fields.push(field);
   return fields;
}


/**
 * Takes CSV content as a string, cleans it and splits to separate rows.
 * @param {String} csvString — string with CSV content.
 * @returns {Array} array of strings.
 */
function getCSVRows(csvString) {
   // remove BOM symbol if any
   let dataStr = csvString.replace(/^\uFEFF/gm, "").replace(/^\u00EF?\u00BB\u00BF/gm,"");

   // remove \r symbols
   dataStr = dataStr.replace(/\r/gm, "");

   // split string with data values into array of rows and filter empty rows
   return dataStr.split(/\n/).filter(v => v.trim().length > 1);
}


/**
 * Drops the rows with nothing but the values separator and spaces, as a spreadsheet writes
 * below the data. The parser dropped them on its own and the guess did not, so such a file
 * failed the guess.
 * @param {Array} rows - array of rows (each row is a string).
 * @param {string} sep - symbol used for separation of values.
 * @returns {Array} array of rows.
 */
function dropSeparatorRows(rows, sep) {
   return rows.filter(v => v.replaceAll(sep, '').trim().length > 0);
}


/**
 * Parse string with text data from a CSV file and create a matrix with values.
 * @param {string} strData - string with all data from the CSV file.
 * @param {string} sep - symbols used for separation of values (by default ',').
 * @param {string} header - type of header (first row in the data).
 * @param {boolean} hasLabels - logical, if first column must be treated as object labels or not.
 * @param {boolean} hasClassNames - logical, if column next to labels must be treated as class names or not.
 * @param {string} varvaluesName - name for variable values (e.g. Wavelength).
 * @param {string} varvaluesUnits - units for variable values (e.g. nm).
 * @param {number} nresp - number of response variables (columns), if -1 it will be set to the number of rows.
 * @returns {Object} JSON containing matrix with parsed values, header, values and decimal separators.
 */
export function parseCSV(strData, sep, header, hasLabels, hasClassNames, varvaluesName, varvaluesUnits, nresp = 0) {


   // set default separator symbol
   if (sep === undefined) {
      sep = ",";
   }

   if (!(sep === ',' || sep === ';')) {
      throw new Error('Values separator can be either comma or semicolon.')
   }

   // setup decimal separator
   const dec = sep === ";" ? "," : ".";

   // split string with data values into array of rows and filter empty rows and rows of
   // separators alone; the count is checked after: a file of separator rows alone reached the
   // first row's split as undefined
   const rows = dropSeparatorRows(getCSVRows(strData), sep);

   if (rows.length < 1) {
      throw new Error('Could not find non-empty rows with values, check your CSV file and try again.');
   }

   // get array with values from the first row
   const firstRowValues = splitCSVRow(rows[0], sep);
   if (firstRowValues.length < 2) {
      throw new Error("Found only one column, looks like your delimiter is not correct.");
   }

   // check if the first row contains header
   let startRow = 0;
   let headerLabels;
   const hasHeader = header !== 'no';
   if (hasHeader) {
      headerLabels = firstRowValues;
      startRow = 1;
   }

   // number of rows and columns in future matrix
   const nrows = rows.length - startRow;
   if (nrows < 1) {
      throw new Error("Data file should contain at least 1 row with values.");
   }

   if (nresp === -1) {
      nresp = nrows;
   }

   // check if first column contains row labels
   let labelsVarname;
   let startColumn;
   let objlabels;
   let objindices = Array(nrows);

   if (hasLabels) {
      labelsVarname = hasHeader ? clearLabels(headerLabels[0]) : '';
      objlabels = Array(nrows);
      startColumn = 1;
   } else{
      labelsVarname = '';
      objlabels = [];
      startColumn = 0;
   }

   // check if next column contains class names
   let classLabelsVarname;
   let classNames = [];
   let classes;

   if (hasClassNames) {
      classLabelsVarname = hasHeader ? clearLabels(headerLabels[startColumn]) : '';
      classes = Array(nrows);
      startColumn = startColumn + 1;
   } else {
      classLabelsVarname = '';
      classes = [];
   }

   // compute number of columns
   const ncols = firstRowValues.length - startColumn;
   if (ncols < 1) {
      throw new Error("Data file should contain at least 1 column with values.");
   }

   // set rows of data as columns of matrix - it is faster
   const data = Matrix.zeros(ncols, nrows);

   // find symbol used for decimals separator
   for (let r = startRow; r < nrows + startRow; r++) {

      // add row/object index
      const rind = r - startRow;
      objindices[rind] = rind;

      // split the row string into array of values
      let rowValuesAll = splitCSVRow(rows[r], sep);

      // if the first element is row label add it to labels array and increase the offset
      if (hasLabels) {
         objlabels[rind] = rowValuesAll[0];
         rowValuesAll.shift()
      }

      // if the next element is class/group label add it to classes array and increase the offset
      if (hasClassNames) {
         // cleaned before it is counted: 'A' and '"A"' were two classes. a row ending before its
         // class gets '' and fails the length check below
         const className = clearLabels(rowValuesAll[0] ?? '');
         classes[rind] = className;
         rowValuesAll.shift()

         // also add it to array of unique class names
         if (classNames.length === 0 || classNames.indexOf(className) < 0) {
            classNames.push(className);
         }
      }

      // for the rest of values, trim spaces, replace separator symbol with dot (.) and convert to numbers
      const rowValues = str2num(rowValuesAll, dec);

      if (hasNaN(rowValues)) {
         throw new Error("Some of the values can not be converted to numbers, check your CSV file and try again.");
      }

      if (rowValues.length != ncols) {
         throw new Error("Wrong number of values in row #" + r);
      }

      data.v.set(new Float64Array(rowValues), (r - startRow) * ncols);
   }


   // generate row labels if they are not provided
   if (!hasLabels || objlabels.length !== nrows) {
      objlabels = Array(nrows);
      for (let i = 0; i < nrows; i++) {
         objlabels[i] = 'O' + (i + 1);
      }
   }

   // remove possible extra quotes around the labels (the classes are cleaned as they are read)
   objlabels = objlabels.map(v => clearLabels(v));

   // handle variable labels, indices and values
   let varlabels;
   let varindices = Array(ncols);
   let varvaluesTemp = Array(ncols);
   if (headerLabels && headerLabels.length === ncols + startColumn) {
      varlabels = headerLabels.map(v => clearLabels(v)).slice(startColumn);
      for (let i = 0; i < ncols; i++) {
         varindices[i] = i >= nresp ? i - nresp : i;
         varvaluesTemp[i] = i >= nresp ? i - nresp + 1: i + 1;
      }
   } else {
      varlabels = Array(ncols);
      for (let i = 0; i < ncols; i++) {
         varindices[i] = i >= nresp ? i - nresp : i;
         varlabels[i] = i >= nresp ? 'V' + (i - nresp + 1) : 'Y' + (i + 1);
         varvaluesTemp[i] = i >= nresp ? i - nresp + 1: i + 1;
      }
   }

   // parse variable labels as numeric values if it is expected
   let varvalues;
   if (header === 'values') {
      varvalues = varlabels.map((v, i) => i < nresp ? i : str2num(v, dec));
      if (hasNaN(varvalues)) {
         throw new Error("Some of the header values can not be converted to numbers, check your CSV file and try again.");
      }
   } else {
      varvalues = varvaluesTemp;
   }


   if (!Array.isArray(varvalues)) varvalues = [];
   if (!varvaluesName) varvaluesName = '';
   if (!varvaluesUnits) varvaluesUnits = '';

   varvaluesName = varvalues.length > 0 && varvaluesName.length > 0 ? varvaluesName : 'Variables';
   varvaluesUnits = varvalues.length > 0 && varvaluesUnits.length > 0 ? varvaluesUnits : '';
   const varAxisTitle = varvaluesName + (varvaluesUnits.length > 0 ? ', ' + varvaluesUnits : '');

   // the first 'nresp' entries stand for the responses, so the direction of the axis is read from
   // the predictors; it was read from the first two entries, and a set with a response and a
   // descending axis said not reversed
   const varrev = varvalues[nresp + 1] < varvalues[nresp];

   const out = {
      values: data.t(),
      nresp,
      header,
      hasLabels,
      hasClassNames,
      labelsVarname,
      classLabelsVarname,
      objlabels,
      objindices,
      varlabels,
      varvalues,
      varindices,
      varrev,
      classes,
      classNames,
      sep,
      dec,
      varvaluesName,
      varvaluesUnits,
      varAxisTitle
   };

   return out;
}


/**
 * Tries to detect if first row of CSV file contains header.
 * @param {Array} rows_num_values - arrat of CSV rows, split to separate values and converted to numbers.
 * @returns {boolean} true if header is detected.
 */
function detectHeader(rows_num_values) {

   // the columns tested are the ones numeric below the first row: a column of row labels or of
   // class names is text in every row, so its first value says nothing about a header. the
   // first two columns used to be skipped instead, whatever they held, so a file of two columns
   // had nothing to test and was reported to have a header always
   const cols = [];
   for (let c = 0; c < rows_num_values[0].length; c++) {
      const cv = rows_num_values.slice(1).map(r => r[c]);
      if (!hasNaN(cv)) cols.push([rows_num_values[0][c], cv]);
   }

   // text over numbers is a header
   if (cols.some(([v1]) => Number.isNaN(v1))) {
      return true;
   }

   // numbers over numbers are a header when every one of them is more than 5 standard
   // deviations away from the values below (a wavelength over absorbances)
   return cols.length > 0 && cols.every(([v1, cv]) => Math.abs((v1 - mean(cv)) / sd(cv)) > 5);
}


/**
 * Detects symbol used to separate values in CSV file.
 * @param {Array} rows - array with rows (each row is a string).
 * @returns {Array} array with three values, symbols for values and decimals separators and number of columns.
 */
function detectSeparators(rows) {

   // guess values separator
   const sep1 = ',';
   const v11 = splitCSVRow(rows[0], sep1);
   const v12 = splitCSVRow(rows[1], sep1);
   const l11 = v11.length;
   const l12 = v12.length;

   const sep2 = ';';
   const v21 = splitCSVRow(rows[0], sep2);
   const v22 = splitCSVRow(rows[1], sep2);
   const l21 = v21.length;
   const l22 = v22.length;

   // check which of the two separators give longer and more consistent
   // arrays with values
   let ncols, v1, v2, sep;
   if (l11 > 1 && l12 > 1 && l11 === l12 && l11 > l21) {
      sep = sep1;
      ncols = l11;
      v1 = v11;
      v2 = v12;
   } else if (l21 > 1 && l22 > 1 && l21 === l22) {
      sep = sep2;
      ncols = l21;
      v1 = v21;
      v2 = v22;
   } else {
      throw new Error('Values in the CSV file should be separated by coma or by semicolon.');
   }

   // decimal separator
   const dec = sep === ',' ? '.' : ',';
   return [sep, dec, ncols];
}


/**
 * Parses CSV file string to detect main parameters (separators, header, presence of row and class labels).
 * @param {String} csvString - string with CSV file content.
 * @returns {Array} array with parameters.
 */
export function parseCSVParams(csvString, nresp = 0) {

   // split string with CSV content to separate rows and check size
   const tooFewRows = 'It looks like the dataset has less then three rows, minimum three is ' +
      'needed, check your CSV file and try again.';
   const rowsAll = getCSVRows(csvString);
   if (rowsAll.length < 3) {
      throw new Error(tooFewRows);
   }

   // detect values and decimal separators
   const [sep, dec, ncols] = detectSeparators(rowsAll);

   // the rows of the separator alone go, and the count is checked again without them
   const rows = dropSeparatorRows(rowsAll, sep);
   const nrows = rows.length;
   if (nrows < 3) {
      throw new Error(tooFewRows);
   }
   const max_ncols = Math.min(ncols, 5);

   // split first several rows to values and keep maximum 5 values in each
   const max_nrows = Math.min(nrows, 5);
   const first_row = splitCSVRow(rows[0], sep);
   const ncols_total = first_row.length;
   let rows_values = rows.slice(0, max_nrows).map(v => splitCSVRow(v, sep).slice(0, max_ncols));

   // additionally clear the values
   rows_values = rows_values.map(v => v.map(v => clearLabels(v)));

   // turn values to numeric and check that values starting from row 2 and column 3 are numeric
   const rows_num_values = rows_values.map(v => str2num(v.slice(), dec));
   for (let i = 1; i < max_nrows; i++) {
      if (hasNaN(rows_num_values[i].slice(2))) {
         throw new Error('Some of the data values expected to be numeric are not, check your CSV file.');
      }
   }

   // detect if the data has header
   const hasHeader = detectHeader(rows_num_values);
   let headerShift = 0;

   const r1 = hasHeader ? 1 : 0;
   const r2 = r1 + 1;

   // values from first column of rows 2 and 3 starting from header
   const v21 = rows_values[r1][0];
   const v31 = rows_values[r2][0];

   // numeric values from first column of rows 2 and 3
   const nv21 = rows_num_values[r1][0];
   const nv31 = rows_num_values[r2][0];

   // numeric values from second column of rows 2 and 3
   const nv22 = rows_num_values[r1][1];
   const nv32 = rows_num_values[r2][1];

   // detect columns with row and class labels
   let hasLabels = false;
   let hasClassLabels = false;
   if (Number.isNaN(nv21) && Number.isNaN(nv31) && v21 !== v31) {
      // guess if row labels present: if first value of rows 2 and 3 are not numbers
      // and are not equal to each other than they are possible row labels
      hasLabels = true;
      headerShift = headerShift + 1;
   } else if (Number.isNaN(nv22) && Number.isNaN(nv32)) {
      // text in column 1, and column 0 not two distinct texts: row labels, then classes.
      // column 0 may repeat here (replicates): 'parseCSV' reads two leading text columns in no
      // other order, and a text column read as a number makes it throw
      hasLabels = true;
      hasClassLabels = true;
      headerShift = headerShift + 2;
   }


   // guess class labels - we check next column and if both values of rows 2 and 3
   // are not numeric, we assume they are class labels
   if (!hasClassLabels) {
      const ncol = hasLabels ? 1 : 0;

      // numeric values from first column of rows 2 and 3
      const nv21 = rows_num_values[r1][ncol];
      const nv31 = rows_num_values[r2][ncol];

      hasClassLabels = hasNaN([nv21, nv31]);
      headerShift = headerShift + hasClassLabels;
   }

   // guess if header contains text labels or numeric values, from the whole first row past the
   // label, class and response columns: the five values kept above ran out with row labels and
   // four responses, and the empty rest said 'values' whatever the header held
   let header = 'no';
   if (hasHeader) {
      const nv1 = str2num(first_row.map(v => clearLabels(v)).slice(headerShift + nresp), dec);
      header = hasNaN(nv1) ? 'labels' : 'values';
   }

   // a class column in column 0 with three rows or fewer per value on average over the rows given,
   // and at least ten of them: replicate row labels, which the first two rows can not tell from
   // classes. the loaders give the first 100 KB, which for a wide file is too few rows to decide
   let fewRowsPerClass = false;
   if (hasClassLabels && !hasLabels) {
      let col = rows.slice(r1).map(v => clearLabels(splitCSVRow(v, sep)[0]));
      // every value must be text, the last line's included: a number among missing values is a
      // damaged numeric column, and any number among them keeps the class guess
      const allText = str2num(col, dec).every(v => Number.isNaN(v));
      // the last line, with no line end after it, may be a row cut at 100 KB: it is left out when
      // no other row has its label, so a cut label is not a value, and leaving it out only makes
      // the count stricter
      const tail = csvString.slice(csvString.lastIndexOf('\n') + 1);
      const last = col[col.length - 1];
      if (dropSeparatorRows(getCSVRows(tail), sep).length > 0 && !col.slice(0, -1).includes(last)) {
         col = col.slice(0, -1);
      }
      fewRowsPerClass = allText && col.length >= 10 && col.length <= 3 * new Set(col).size;
   }

   return [sep, hasLabels, hasClassLabels, header, ncols_total, fewRowsPerClass];
}


/**
 * Tells if a row holds one column more than a model needs besides the class column. The loaders
 * then assume it is a leading column of numeric row labels the text guess missed; a stray extra
 * column elsewhere looks the same.
 * @param {number} ncolsTotal - number of fields in the first row of the file.
 * @param {number} ncolsData - number of data columns the model needs.
 * @param {boolean} hasClassLabels - if the file has a class column.
 * @returns {boolean}
 */
export function labelsByWidth(ncolsTotal, ncolsData, hasClassLabels) {
   return ncolsTotal - (hasClassLabels ? 1 : 0) === ncolsData + 1;
}


/**
 * Converts dataset object to CSV string.
 * @param {Object} dataset - object with dataset.
 * @returns {String}
 */
export function toCSV(dataset) {

   // get values then transpose
   const values = dataset.values.t();
   const sep = dataset.sep;
   const dec = dataset.dec;

   let header_values = null;
   if (dataset.header !== 'no') {
      header_values = dataset.header === 'values' ?
         (dataset.nresp  && dataset.nresp > 0 ?
            dataset.varlabels.slice(0, dataset.nresp).concat(dataset.varvalues.slice(dataset.nresp)) : dataset.varvalues.slice())
         : dataset.varlabels.slice();
   }

   if (header_values && dataset.hasClassNames) {
      header_values = [dataset.classLabelsVarname, ...header_values];
   }

   if (header_values && dataset.hasLabels) {
      header_values = [dataset.labelsVarname, ...header_values];
   }

   // the dataset.hasX flags must be checked here as well, otherwise the columns written below
   // disagree with the header fields added above — parseCSV always fills objlabels with
   // synthetic names (O1, O2, ...) for display, even when the file had no label column
   const hasClassNames = dataset.hasClassNames && dataset.classes && dataset.classes.length === values.ncols;
   const hasLabels = dataset.hasLabels && dataset.objlabels && dataset.objlabels.length === values.ncols;

   // a number is written with the file's decimal separator as its cell is built, text as it is.
   // one replace used to run over the finished text and rewrote every dot in it, so a label
   // 'Peak.A' came out as 'Peak,A' in a file with a decimal comma. a value which is not finite
   // (a one-row set scaled has none) is written as the results writer in methods/simca.js writes
   // it, not in the javascript spelling
   const num2str = v => {
      if (Number.isNaN(v)) return '-';
      if (v === Infinity) return 'Inf';
      if (v === -Infinity) return '-Inf';
      return dec === '.' ? String(v) : String(v).replace('.', dec);
   };
   const cell = v => typeof v === 'number' ? num2str(v) : v;

   // the lines are joined at the end: a file without a header began with an empty line
   const lines = header_values ? [header_values.map(cell).join(sep)] : [];

   for (let i = 0; i < values.ncols; i++) {
      let rowStr = Array.from(values.getcolref(i + 1), num2str).join(sep);

      if (hasClassNames) {
         rowStr = dataset.classes[i] + sep + rowStr;
      }

      if (hasLabels) {
         rowStr = dataset.objlabels[i] + sep + rowStr;
      }

      lines.push(rowStr);
   }

   return lines.join('\n') + '\n';
}


/**
 * Convert dataset to CSV and starts downloading process.
 * @param {Object} dataset - JSON with dataset.
 * @param {String} filename - filename (without extension).
 */
export function downloadData(dataset, filename) {
   const csvString = toCSV(dataset);
   downloadCSV(csvString, filename);
}


/**
 * Class name cut to a given length: a longer one keeps its first characters, without trailing
 * spaces, and an ellipsis.
 * @param {string} name - the class name.
 * @param {number} max - the longest name shown whole.
 * @param {number} keep - number of characters a cut keeps.
 * @returns {string} the name, or its first 'keep' characters, without trailing spaces, and '…'.
 */
export function cutName(name, max, keep) {
   return name.length > max ? name.slice(0, keep).trimEnd() + '…' : name;
}


/**
 * Class name as tick labels show it: at most 10 characters, else its first 9 characters, without
 * trailing spaces, and an ellipsis.
 * @param {string} name - the class name.
 * @returns {string} the name as 'cutName' gives it.
 */
export function shortName(name) {
   return cutName(name, 10, 9);
}


/**
 * User text as a plot text: svelte-plots-basic draws its texts as HTML, so a '<' that would open
 * a tag ('<LOD') and an '&' that could open an entity are escaped. A character right after '_' or
 * '^' (or '_-', '^-') is left as it is: the parser draws it scripted, which works today.
 * @param {string} s - the text; anything else is returned as it is.
 * @returns {string} the text to pass to a plot.
 */
export function escapeText(s) {
   if (typeof s !== 'string') return s;
   return s.replace(/(?<![_^]-?)&(?=[A-Za-z#])/g, '&amp;')
      .replace(/(?<![_^]-?)<(?=[A-Za-z/!?])/g, '&lt;');
}


/**
 * Joins the fields of one row of a CSV file. A field holding the separator, a quote or a line
 * break is quoted, its quotes doubled; a missing field is empty.
 * @param {Array} fields - the values of the row.
 * @param {String} sep - the field separator.
 * @param {String} [dec='.'] - the decimal mark written in a number; a string is kept as it is.
 * @returns {String} the row with its line break.
 */
export function csvRow(fields, sep, dec = '.') {
   const cell = (v) => {
      const s = v == null ? '' : typeof v === 'number' ? String(v).replace('.', dec) : String(v);
      return /["\r\n]/.test(s) || s.includes(sep) ? '"' + s.replace(/"/g, '""') + '"' : s;
   };
   return fields.map(cell).join(sep) + '\n';
}


/**
 * Starts downloading a CSV string as a file.
 * @param {String} csvString - the file content.
 * @param {String} filename - filename (without extension).
 */
export function downloadCSV(csvString, filename) {
   const blob = new Blob([csvString], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `${filename}.csv`);
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Start downloading of JSON object as a file.
 * @param {Object} obj - JSON with object to download.
 */
export function downloadJSON(obj, fileName) {

   const blob = new Blob([JSON.stringify(obj, replacer)], { type: 'application/json' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', fileName + '.json');
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Create a new dataset object by excluding variables from the original one.
 * @param {Object} dataset - dataset with original values.
 * @param {Array} varlist - list of variables to exclude.
 * @returns {Object}
 */
export function excludeVars(dataset, varlist) {
   if (!dataset) return;
   const nresp = dataset.nresp ? dataset.nresp : 0;
   const values = dataset.values;
   const varvalues = dataset.varvalues;
   const varlabels = dataset.varlabels;
   const varindices = dataset.varindices;

   const nrows = values.nrows;
   const ncols = values.ncols;
   const ncolsNew = ncols - varlist.length
   const valuesNew = Matrix.zeros(nrows, ncolsNew);
   const varvaluesNew = Array(ncolsNew);
   const varlabelsNew = Array(ncolsNew);
   const varindicesNew = Array(ncolsNew);

   let c = 0;
   for (let i = 0; i < nresp; i++) {
      const offset_0 = nrows * i;
      const offset_1 = nrows * c;
      valuesNew.v.set(values.v.subarray(offset_0, offset_0 + nrows), offset_1);
      varvaluesNew[c] = varvalues[i];
      varlabelsNew[c] = varlabels[i];
      varindicesNew[c] = varindices[i];
      c = c + 1;
   }

   const varSet = new Set(varlist);
   for (let i = nresp; i < ncols; i++) {
      if (varSet.has(i - nresp)) continue;
      const offset_0 = nrows * i;
      const offset_1 = nrows * c;
      valuesNew.v.set(values.v.subarray(offset_0, offset_0 + nrows), offset_1);
      varvaluesNew[c] = varvalues[i];
      varlabelsNew[c] = varlabels[i];
      varindicesNew[c] = varindices[i];
      c = c + 1;
   }

   return {
      values: valuesNew,
      header: dataset.header,
      hasLabels: dataset.hasLabels,
      hasClassNames: dataset.hasClassNames,
      classNames: dataset.classNames,
      labelsVarname: dataset.labelsVarname,
      classLabelsVarname: dataset.classLabelsVarname,
      objlabels: dataset.objlabels,
      objindices: dataset.objindices,
      varlabels : varlabelsNew,
      varvalues : varvaluesNew,
      varindices: varindicesNew,
      varrev: dataset.varrev,
      classes: dataset.classes,
      sep: dataset.sep,
      dec: dataset.dec,
      varvaluesName: dataset.varvaluesName,
      varvaluesUnits: dataset.varvaluesUnits,
      varAxisTitle: dataset.varAxisTitle,
      fileName: dataset.fileName,
      nresp: nresp
   };
}


/**
 * Create a new dataset object by excluding outliers from the original one.
 * @param {Object} dataset - dataset with original values.
 * @param {Array} outliers - list of outliers to exclude.
 * @returns {Object}
 */
export function excludeOutliers(dataset, outliers) {

   if (!outliers) return dataset;

   // the type check has to come BEFORE the length test, not after it: an object carrying a
   // 'length' of 0 would otherwise return here as if it were an empty list and never be seen.
   // this path builds a new dataset from the filtered list rather than handing it to a wrapper,
   // so a bad value leaves the outlier sitting in the "dataset without outliers". the message is
   // raised here rather than left to 'handleOutliers' so it names the function the caller used
   if (!Array.isArray(outliers)) {
      throw new Error('excludeOutliers: indices in "outliers" array must be whole numbers.');
   }

   const outlierValues = Array.from(outliers);
   if (!outlierValues.every(Number.isInteger)) {
      throw new Error('excludeOutliers: indices in "outliers" array must be whole numbers.');
   }

   if (!outlierValues.every(v => v >= 0 && v < dataset.values.nrows)) {
      throw new Error('excludeOutliers: some indices in "outliers" array are outside the expected range.');
   }

   if (outlierValues.length === 0) return dataset;

   const [objindices, objlabels, classes, classNames] = handleOutliers(dataset, outlierValues);
   const values = dataset.values.subset(objindices.map(v => v + 1), []);

   return {
      values: values,
      objlabels: objlabels,
      objindices: objindices,
      classes: classes,
      classNames: classNames,

      header: dataset.header,
      hasLabels: dataset.hasLabels,
      hasClassNames: dataset.hasClassNames,
      labelsVarname: dataset.labelsVarname,
      classLabelsVarname: dataset.classLabelsVarname,
      varlabels : dataset.varlabels,
      varvalues : dataset.varvalues,
      varindices: dataset.varindices,
      varrev: dataset.varrev,
      sep: dataset.sep,
      dec: dataset.dec,
      varvaluesName: dataset.varvaluesName,
      varvaluesUnits: dataset.varvaluesUnits,
      varAxisTitle: dataset.varAxisTitle,
      fileName: dataset.fileName,
      nresp: dataset.nresp
   };
}


/**
 * Converts array of numbers to string with intervals (e.g. [1, 2, 3, 4, 10, 11, 12] -> "1-4, 10-12").
 * @param {Array} arr - array of numbers.
 * @returns {String}
 */
export function convertToIntervals(arr) {
  if (!arr.length) return '';

  // Sort the array in ascending order
  arr = [...arr].sort((a, b) => a - b);

  let result = [];
  let start = arr[0];
  let end = start;

  for (let i = 1; i < arr.length; i++) {
    if (arr[i] === end + 1) {
      // continue the sequence
      end = arr[i];
    } else {
      // sequence broken, push current range to result
      if (start === end) {
        result.push(`${start}`);
      } else {
        result.push(`${start}-${end}`);
      }
      // reset start and end for the next sequence
      start = arr[i];
      end = start;
    }
  }

  // push the last range or number
  if (start === end) {
    result.push(`${start}`);
  } else {
    result.push(`${start}-${end}`);
  }

  return result.join(" ");
}


/**
 * Manual replacer for JSON.stringify() to handle matrices, vectors and typed arrays correctly.
 * @param {string} key - key
 * @param {Object} value - value
 * @returns {Object} JSON with proper structure, so it can be converted back by reviver.
 */
export function replacer(key, value) {


   // objects of Matrix class from mdatools/arrays
   if (ismatrix(value)) {
      return {
         __type: 'matrix',
         nrows: value.nrows,
         ncols: value.ncols,
         v: storeValues(value.v, true)
      };
   }

   // objects of Vector class from mdatools/arrays
   if (isvector(value)) {
      return {
         __type: 'vector',
         length: value.length,
         v: storeValues(value.v, true)
      };
   }

   // Typed arrays
   if (ArrayBuffer.isView(value)) {
      return {
         __type: value.constructor.name,
         data: storeValues(value, FLOAT_ARRAYS.has(value.constructor))
      };
   }

   // a NaN or an infinity in a plain field or list: JSON writes null for it, and null it stayed
   if (typeof value === 'number' && !Number.isFinite(value)) {
      return { __type: 'number', v: String(value) };
   }

   return value;
}


// JSON has no NaN and no infinities: they are written under these names, and read back by them.
// a null inside float data was a non-finite value written before the names: JSON wrote null for
// a NaN and an infinity alike, so it can only come back as NaN
const NON_FINITE = Object.assign(Object.create(null),
   { NaN: NaN, Infinity: Infinity, '-Infinity': -Infinity });
const FLOAT_ARRAYS = new Set([Float64Array, Float32Array]);

// the values of a typed array, or of a matrix's or a vector's 'v', as they are written
function storeValues(values, floats) {
   return floats ? Array.from(values, x => Number.isFinite(x) ? x : String(x)) : Array.from(values);
}

// the values read back: a list of finite numbers, plus the three names and null for float data.
// anything else is a damaged file; the typed array constructor took it in silence, as empty, as
// 0, as NaN or as a number of its own
function readValues(values, tag, floats) {
   if (!Array.isArray(values)) throw new Error(`reviver: the "${tag}" data is not a list.`);
   return values.map(x => {
      if (typeof x === 'number' && Number.isFinite(x)) return x;
      if (floats && x === null) return NaN;
      if (floats && typeof x === 'string' && x in NON_FINITE) return NON_FINITE[x];
      throw new Error(`reviver: the "${tag}" data holds a value which is not a number.`);
   });
}


// the typed-array tags this reviver supports. NOT every tag 'replacer' can emit - it also tags a
// DataView and an empty BigInt array, both of which are refused below. the lookup used to go
// through 'globalThis', which resolves hundreds of unrelated callables: a damaged tag of "Array"
// built an array containing the array, silently. a plain object would not be enough either, since
// it inherits 'Object.prototype', where a '__type' of "constructor" resolves to 'Object'
const TYPED_ARRAYS = Object.assign(Object.create(null), {
   Float64Array, Float32Array,
   Int8Array, Uint8Array, Uint8ClampedArray,
   Int16Array, Uint16Array,
   Int32Array, Uint32Array,
});


/**
 * Manual reviver for parseJSON() to handle matrices, vectors and typed arrays correctly
 * @param {string} key - key
 * @param {JSON} value - value
 * @returns {Object} correctly recovered object.
 */
export function reviver(key, value) {

   // presence, not truthiness: an empty '__type' carrying 'data' is a damaged typed array and has
   // to reach the check below rather than pass through
   if (value && typeof value === "object" && Object.hasOwn(value, '__type')) {

      // the constructor counts the values against the size, and takes a size which is not a
      // whole number, or a size below zero, as it is
      if (value.__type === 'matrix') {
         const { nrows, ncols } = value;
         if (!Number.isInteger(nrows) || !Number.isInteger(ncols) || nrows < 1 || ncols < 1) {
            throw new Error('reviver: the "matrix" size is damaged.');
         }
         return matrix(readValues(value.v, 'matrix', true), nrows, ncols);
      }

      if (value.__type === 'vector') {
         const v = readValues(value.v, 'vector', true);
         if (value.length !== v.length) throw new Error('reviver: the "vector" length is damaged.');
         return vector(v);
      }

      if (value.__type === 'number') {
         if (typeof value.v === 'string' && value.v in NON_FINITE) return NON_FINITE[value.v];
         throw new Error('reviver: the "number" value is damaged.');
      }

      const TypedArrayConstructor = TYPED_ARRAYS[value.__type];
      if (TypedArrayConstructor) {
         const floats = FLOAT_ARRAYS.has(TypedArrayConstructor);
         return new TypedArrayConstructor(readValues(value.data, value.__type, floats));
      }

      // a tag carrying 'data' is 'replacer's typed-array shape, so an unknown one is a damaged
      // file rather than someone else's marker. tags without 'data' - nimca's 'NIMCA' - pass
      // through, which its ModelLoader relies on
      if (Object.hasOwn(value, 'data')) {
         throw new Error(`reviver: unsupported data type "${value.__type}".`);
      }
    }

    return value;
}


// arrays with at least this many values are stored as records of their own; test builds lower it
export const SPLIT_MIN = typeof __SPLIT_MIN__ === 'number' ? __SPLIT_MIN__ : 100000;

// the types a record can hold; a type's place seeds the hash, so two types never share a record
const SPLIT_TYPES = ['matrix', 'vector', ...Object.keys(TYPED_ARRAYS)];


/**
 * MurmurHash3 x86_128 of 'bytes' (a Uint8Array), as 32 hex digits: h1..h4, 8 digits each.
 */
export function murmur128(bytes, seed = 0) {
   // the body reads 32-bit words, which need an offset that is a multiple of four
   if (bytes.byteOffset & 3) bytes = bytes.slice();
   const nbytes = bytes.byteLength;
   const nblocks = nbytes >>> 4;
   const u = new Uint32Array(bytes.buffer, bytes.byteOffset, nblocks * 4);
   let h1 = seed, h2 = seed, h3 = seed, h4 = seed;
   const c1 = 0x239b961b, c2 = 0xab0e9789, c3 = 0x38b34ae5, c4 = 0xa1e38b93;
   for (let i = 0; i < u.length; i += 4) {
      let k1 = u[i], k2 = u[i + 1], k3 = u[i + 2], k4 = u[i + 3];
      k1 = Math.imul(k1, c1); k1 = (k1 << 15) | (k1 >>> 17); k1 = Math.imul(k1, c2); h1 ^= k1;
      h1 = (h1 << 19) | (h1 >>> 13); h1 = (h1 + h2) | 0; h1 = (Math.imul(h1, 5) + 0x561ccd1b) | 0;
      k2 = Math.imul(k2, c2); k2 = (k2 << 16) | (k2 >>> 16); k2 = Math.imul(k2, c3); h2 ^= k2;
      h2 = (h2 << 17) | (h2 >>> 15); h2 = (h2 + h3) | 0; h2 = (Math.imul(h2, 5) + 0x0bcaa747) | 0;
      k3 = Math.imul(k3, c3); k3 = (k3 << 17) | (k3 >>> 15); k3 = Math.imul(k3, c4); h3 ^= k3;
      h3 = (h3 << 15) | (h3 >>> 17); h3 = (h3 + h4) | 0; h3 = (Math.imul(h3, 5) + 0x96cd1c35) | 0;
      k4 = Math.imul(k4, c4); k4 = (k4 << 18) | (k4 >>> 14); k4 = Math.imul(k4, c1); h4 ^= k4;
      h4 = (h4 << 13) | (h4 >>> 19); h4 = (h4 + h1) | 0; h4 = (Math.imul(h4, 5) + 0x32ac3b17) | 0;
   }
   const tail = new Uint8Array(bytes.buffer, bytes.byteOffset + nblocks * 16, nbytes & 15);
   let k1 = 0, k2 = 0, k3 = 0, k4 = 0;
   switch (tail.length) {
      case 15: k4 ^= tail[14] << 16;
      case 14: k4 ^= tail[13] << 8;
      case 13: k4 ^= tail[12];
         k4 = Math.imul(k4, c4); k4 = (k4 << 18) | (k4 >>> 14); k4 = Math.imul(k4, c1); h4 ^= k4;
      case 12: k3 ^= tail[11] << 24;
      case 11: k3 ^= tail[10] << 16;
      case 10: k3 ^= tail[9] << 8;
      case 9: k3 ^= tail[8];
         k3 = Math.imul(k3, c3); k3 = (k3 << 17) | (k3 >>> 15); k3 = Math.imul(k3, c4); h3 ^= k3;
      case 8: k2 ^= tail[7] << 24;
      case 7: k2 ^= tail[6] << 16;
      case 6: k2 ^= tail[5] << 8;
      case 5: k2 ^= tail[4];
         k2 = Math.imul(k2, c2); k2 = (k2 << 16) | (k2 >>> 16); k2 = Math.imul(k2, c3); h2 ^= k2;
      case 4: k1 ^= tail[3] << 24;
      case 3: k1 ^= tail[2] << 16;
      case 2: k1 ^= tail[1] << 8;
      case 1: k1 ^= tail[0];
         k1 = Math.imul(k1, c1); k1 = (k1 << 15) | (k1 >>> 17); k1 = Math.imul(k1, c2); h1 ^= k1;
   }
   h1 ^= nbytes; h2 ^= nbytes; h3 ^= nbytes; h4 ^= nbytes;
   h1 = (h1 + h2) | 0; h1 = (h1 + h3) | 0; h1 = (h1 + h4) | 0;
   h2 = (h2 + h1) | 0; h3 = (h3 + h1) | 0; h4 = (h4 + h1) | 0;
   const fmix = (h) => {
      h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
      return h ^ (h >>> 16);
   };
   h1 = fmix(h1); h2 = fmix(h2); h3 = fmix(h3); h4 = fmix(h4);
   h1 = (h1 + h2) | 0; h1 = (h1 + h3) | 0; h1 = (h1 + h4) | 0;
   h2 = (h2 + h1) | 0; h3 = (h3 + h1) | 0; h4 = (h4 + h1) | 0;
   const hex = (h) => (h >>> 0).toString(16).padStart(8, '0');
   return hex(h1) + hex(h2) + hex(h3) + hex(h4);
}


/**
 * Key of the record holding the values of an array of 'type': 'a:<hash>-<length>'.
 */
export function arrayKey(type, values) {
   const bytes = new Uint8Array(values.buffer, values.byteOffset, values.byteLength);
   return 'a:' + murmur128(bytes, SPLIT_TYPES.indexOf(type) + 1) + '-' + values.length;
}


/**
 * Serialises a session for the browser store: matrices, vectors and typed arrays with at least
 * 'min' values become tags with a 'ref' and are returned in 'arrays' (key -> {type, values});
 * everything else goes to 'appReplacer'.
 */
export function splitSession(session, appReplacer = replacer, min = SPLIT_MIN) {
   const arrays = new Map();
   const seen = new Map();                  // type -> Map of array -> key, to hash each array once
   const take = (type, values) => {
      if (!seen.has(type)) seen.set(type, new Map());
      const known = seen.get(type);
      if (known.has(values)) return known.get(values);
      // a view of part of a buffer would store the whole buffer
      const own = values.byteOffset === 0 && values.byteLength === values.buffer.byteLength ?
         values : values.slice();
      const key = arrayKey(type, own);
      arrays.set(key, { type, values: own });
      known.set(values, key);
      return key;
   };

   const skeleton = JSON.stringify(session, function (key, value) {
      if (ismatrix(value) && value.v.length >= min) {
         const ref = take('matrix', value.v);
         return { __type: 'matrix', nrows: value.nrows, ncols: value.ncols, ref };
      }
      if (isvector(value) && value.v.length >= min) {
         return { __type: 'vector', length: value.length, ref: take('vector', value.v) };
      }
      if (ArrayBuffer.isView(value) && TYPED_ARRAYS[value.constructor.name] === value.constructor &&
            value.length >= min) {
         return { __type: value.constructor.name, ref: take(value.constructor.name, value) };
      }
      return appReplacer.call(this, key, value);
   });

   return { skeleton, arrays };
}


// a tag which stands for a stored array
const isRefTag = (value) => value !== null && typeof value === 'object' &&
   Object.hasOwn(value, '__type') && Object.hasOwn(value, 'ref');


/**
 * Returns the set of records a session skeleton refers to. Throws on text which is not json.
 */
export function refsOf(skeleton) {
   const refs = new Set();
   const walk = (value) => {
      if (value === null || typeof value !== 'object') return;
      // loops, not 'Object.values', which in WebKit took 179 s on an array of 20M values
      if (Array.isArray(value)) {
         for (let i = 0; i < value.length; i++) walk(value[i]);
         return;
      }
      if (isRefTag(value) && typeof value.ref === 'string') refs.add(value.ref);
      for (const key in value) walk(value[key]);
   };
   walk(JSON.parse(skeleton));
   return refs;
}


/**
 * Parses a session skeleton, building each tag with a 'ref' from its record in 'records' (ref ->
 * values); everything else goes to 'appReviver'. Throws on a damaged reference or record.
 */
export function joinSession(skeleton, records, appReviver = reviver) {
   const used = new Set();
   return JSON.parse(skeleton, function (key, value) {
      if (!isRefTag(value)) return appReviver.call(this, key, value);

      const { __type: type, ref } = value;
      const m = typeof ref === 'string' ? /^a:[0-9a-f]{32}-(\d+)$/.exec(ref) : null;
      if (!m) throw new Error('joinSession: the reference is damaged.');
      let values = records.get(ref);
      if (values === undefined) throw new Error('joinSession: a record is missing.');

      const T = type === 'matrix' || type === 'vector' ? Float64Array : TYPED_ARRAYS[type];
      if (!T) throw new Error(`joinSession: unsupported data type "${type}".`);
      if (!ArrayBuffer.isView(values) || values.constructor !== T) {
         throw new Error(`joinSession: the "${type}" record has the wrong type.`);
      }

      let length = Number(m[1]);
      if (type === 'matrix') {
         const { nrows, ncols } = value;
         if (!Number.isInteger(nrows) || !Number.isInteger(ncols) || nrows < 1 || ncols < 1) {
            throw new Error('joinSession: the "matrix" size is damaged.');
         }
         if (nrows * ncols !== length) length = -1;
      }
      if (type === 'vector' && value.length !== length) length = -1;
      if (values.length !== length) {
         throw new Error(`joinSession: the "${type}" record has the wrong length.`);
      }

      // no two objects share a buffer
      if (used.has(ref)) values = values.slice();
      used.add(ref);

      if (type === 'matrix') return new Matrix(values, value.nrows, value.ncols);
      if (type === 'vector') return new Vector(values);
      return values;
   });
}


// the first bytes of a session file; the digit is the version of the format
export const SESSION_MAGIC = 'MDATOOLS-SESSION-FILE-1\n';
const SESSION_PREFIX = 'MDATOOLS-SESSION-FILE-';

const ascii = (s) => Uint8Array.from(s, (c) => c.charCodeAt(0));
const pad8 = (n) => (8 - n % 8) % 8;
const recordArray = (t) => t === 'matrix' || t === 'vector' ? Float64Array : TYPED_ARRAYS[t];


/**
 * The bytes of a session file, as parts for a Blob: magic, header length (uint32), header hash
 * (32 hex digits), header (UTF-8 JSON with 'app', 'skeleton' and 'records'), zeros to a multiple
 * of 8, then each record's values, each starting at a multiple of 8 from the start of the data.
 * 'skeleton' and 'arrays' are what 'splitSession' returns.
 */
export function sessionFileParts(skeleton, arrays, app) {
   const records = [], data = [];
   let offset = 0;
   for (const [key, { type, values }] of arrays) {
      const pad = pad8(offset);
      if (pad) data.push(new Uint8Array(pad));
      offset += pad;
      records.push({ key, type, offset, length: values.length });
      data.push(new Uint8Array(values.buffer, values.byteOffset, values.byteLength));
      offset += values.byteLength;
   }
   const header = new TextEncoder().encode(JSON.stringify({ app, skeleton, records }));
   const length = new Uint8Array(4);
   new DataView(length.buffer).setUint32(0, header.length, true);
   return [ascii(SESSION_MAGIC), length, ascii(murmur128(header)), header,
      new Uint8Array(pad8(60 + header.length)), ...data];
}


/**
 * Reads a session file written by 'sessionFileParts'. 'read(start, end)' resolves to the bytes
 * [start, end) of a file of 'size' bytes as an ArrayBuffer. Resolves to {skeleton, records}, the
 * records as a Map key -> typed array, or to null if the file is not of this format at all.
 * A read that fails rejects as it is; any other failure is an Error with 'kind' 'newer' (another
 * version of the format), 'refused' (damaged) or 'tooLarge' (too large for the browser).
 */
export async function decodeSessionFile(read, size) {
   const head = new Uint8Array(await read(0, Math.min(24, size)));
   const text = String.fromCharCode(...head);
   if (text !== SESSION_MAGIC) {
      if (!text.startsWith(SESSION_PREFIX)) return null;
      throw sessionFileError('newer');
   }

   // a check that fails, or throws, refuses the file; one the browser has no room for says so
   const check = (fn) => {
      let ok;
      try {
         ok = fn();
      } catch (e) {
         throw sessionFileError(isTooLarge(e) ? 'tooLarge' : 'refused', e);
      }
      if (!ok) throw sessionFileError('refused');
      return ok;
   };

   check(() => size >= 60);
   const lengthHash = new Uint8Array(await read(24, 60));
   check(() => lengthHash.byteLength === 36);
   const len = new DataView(lengthHash.buffer).getUint32(0, true);
   const start = 60 + len + pad8(60 + len);
   check(() => start <= size);

   const bytes = new Uint8Array(await read(60, 60 + len));
   const header = check(() => {
      if (bytes.byteLength !== len) return false;
      if (murmur128(bytes) !== String.fromCharCode(...lengthHash.subarray(4))) return false;
      const h = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
      return h !== null && typeof h === 'object' && typeof h.skeleton === 'string' &&
         Array.isArray(h.records) && h;
   });

   const seen = new Set();
   const records = new Map();
   for (const r of header.records) {
      const end = check(() => {
         if (r === null || typeof r !== 'object') return false;
         if (typeof r.key !== 'string' || !/^a:[0-9a-f]{32}-\d+$/.test(r.key)) return false;
         if (seen.has(r.key) || !SPLIT_TYPES.includes(r.type)) return false;
         if (!Number.isSafeInteger(r.offset) || !Number.isSafeInteger(r.length)) return false;
         if (r.offset < 0 || r.length < 0 || r.offset % 8 !== 0) return false;
         const last = start + r.offset + r.length * recordArray(r.type).BYTES_PER_ELEMENT;
         return last <= size && last;
      });
      seen.add(r.key);
      const a = start + r.offset;
      const buffer = await read(a, end);
      records.set(r.key, check(() => {
         if (buffer.byteLength !== end - a) return false;
         const values = new (recordArray(r.type))(buffer);
         return arrayKey(r.type, values) === r.key && values;
      }));
   }

   return { skeleton: header.skeleton, records };
}


/**
 * True if 'e' says a value is too large for the browser: a RangeError, Firefox's InternalError,
 * or the plain string 'out of memory' that Firefox throws. A stack overflow is not: it comes from
 * deep nesting, which no session has.
 */
export function isTooLarge(e) {
   if (typeof e?.message === 'string' && /call stack|recursion/i.test(e.message)) return false;
   return e instanceof RangeError || e?.name === 'InternalError' || e === 'out of memory';
}


const SESSION_FILE_ERRORS = {
   newer: 'The session file has another version.',
   refused: 'The file is not a session file, or it is damaged.',
   tooLarge: 'The file is too large.',
};

/**
 * An Error with 'kind' ('newer', 'refused' or 'tooLarge'), as 'sessionFileMessage' reads it.
 */
export function sessionFileError(kind, cause) {
   return Object.assign(new Error(SESSION_FILE_ERRORS[kind], { cause }), { kind });
}


/**
 * False if the first bytes of a file rule JSON out: an empty file, a byte order mark with nothing
 * after it, or a first byte after JSON whitespace which is not '{'. Bytes after a mark, and bytes
 * which are all whitespace, are left for the parse to judge.
 */
export function couldBeJson(bytes) {
   if (bytes.length === 0) return false;
   const marks = [[0xEF, 0xBB, 0xBF], [0xFF, 0xFE], [0xFE, 0xFF]];
   const mark = marks.find((m) => m.every((x, i) => bytes[i] === x));
   if (mark) return bytes.length > mark.length;
   const i = bytes.findIndex((x) => x !== 0x20 && x !== 0x09 && x !== 0x0A && x !== 0x0D);
   return i === -1 || bytes[i] === 0x7B;
}


/**
 * The message for a session file that could not be loaded, by the error's 'kind'.
 */
export function sessionFileMessage(e) {
   switch (e?.kind) {
      case 'newer': return 'This session file was written by a newer version of the app.';
      case 'refused': return 'The file does not contain a session for this app.';
      case 'tooLarge': return 'The file is too large to be read in this browser.';
      default: return 'The file could not be read.';
   }
}


/**
 * Converts date/time object to string.
 * @param {Date} date
 * @returns {string}
 */
export function date2str(date) {
   const year = date.getFullYear();
   const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
   const day = String(date.getDate()).padStart(2, '0');
   const hours = String(date.getHours()).padStart(2, '0');
   const minutes = String(date.getMinutes()).padStart(2, '0');
   const seconds = String(date.getSeconds()).padStart(2, '0');

   return `${year}${month}${day}${hours}${minutes}${seconds}`;
}


/**
 * Iterate over top level keys of JSON object and produce another JSON
 * with the same keys and values derived by applying a function to the original values.
 * @param {JSON} obj - original object.
 * @param {function} f - lambda functon with two parameters, object and key, which returns a value.
 * @returns {Object} preprocessed JSON
 */
export function objmap(obj, f) {
   return Object.keys(obj).reduce((acc, id) => {
      acc[id] = f(obj, id);
      return acc;
   }, {});
}


/**
 * Checks if JSON is empty.
 * @param {Object} obj JSON under questions.
 * @returns {boolean} true if is empty.
 */
export function isempty(obj) {
   return !obj || Object.keys(obj).length < 1;
}


/**
 * Converts byte to individual bit values.
 * @param {*} byte
 * @returns
 */
function byteToBits(byte) {
  return Array.from({ length: 8 }, (_, i) => (byte >> (7 - i)) & 1).reverse();
}


/**
 * Reads several bytes from binary data.
 * @param {DataView} - view interface for array buffer with binary data.
 * @param {number} len - number of bytes to read.
 * @param {number} offset - initial offset.
 * @returns {String} the read bytes as characters (string).
 */
function readString(view, len, offset) {
   let str = "";
   for (let i = 0; i < len; i++) {
      const charCode = view.getUint8(offset++);
      if (charCode === 0) break;
      str += String.fromCharCode(charCode);
   }
   return str;
}


/**
 * Parser for SPC file header.
 * @param {DataView} - view interface for array buffer with header as binary data.
 * @returns {Object} JSON with header elements and values.
 */
function parseSPCHeader(view) {

   const xaxisUnits = ["A.U.", "cm^–1", "µm", "nm", "s", "min", "Hz", "KHz", "MHz", "M/z", "ppm", "days",
      "years", "cm^–1", "eV", "", "", "", "", "F", "C", "K", "", "msec", "µsec", "nsec", "GHz", "cm",
      "m", "mm", "h", ""
   ];

   const xaxisNames = [ "Arbitrary", "Wavenumber", "Micrometers", "Nanometers", "Seconds",
      "Minutes", "Hertz", "Kilohertz", "Megahertz", "Mass", "Parts per million", "Days", "Years",
      "Raman shift", "Electron Volts", "X,Y,Z text labels in fcatxt (old 4Dh version only)",
      "Diode Number", "Channel", "Degrees", "Temperature", "Temperature", "Temperature",
      "Data Points", "Milliseconds", "Microseconds", "Nanoseconds", "Gigahertz",
      "Centimeters", "Meters", "Millimeters", "Hours", "Double interferogram (no display labels)"
   ];

   const yaxisNames = [
      "Arbitrary Intensity",         // 0
      "Interferogram",               // 1
      "Absorbance",                  // 2
      "Kubelka-Munk",                // 3
      "Counts",                      // 4
      "Volts",                       // 5
      "Degrees",                     // 6
      "Milliamps",                   // 7
      "Millimeters",                 // 8
      "Millivolts",                  // 9
      "Log (1/R)",                   // 10
      "Percent",                     // 11
      "Intensity",                   // 12
      "Relative Intensity",          // 13
      "Energy",                      // 14
      null,                          // 15 – Not used
      "Decibel",                     // 16
      null,                          // 17 – Not used
      null,                          // 18 – Not used
      "Temperature (F)",             // 19
      "Temperature (C)",             // 20
      "Temperature (K)",             // 21
      "Index of Refraction [N]",     // 22
      "Extinction Coeff. [K]",       // 23
      "Real",                        // 24
      "Imaginary",                   // 25
      "Complex",                     // 26
      /* ... values 27–127 are undefined/unused in the spec ... */
      /* Skip to 128+ */
   ];

   const yaxisNamesExt = [
      "Transmission",                // 128
      "Reflectance",                 // 129
      "Arbitrary or Single Beam with Valley Peaks", // 130
      "Emission"                     // 131
   ];

   let offset = 0;

   // flag bits
   const ftflgs = view.getUint8(offset); offset += 1;

   // file version
   const fversn = view.getUint8(offset); offset += 1;
   if (fversn === 0x4D) {
      throw new Error('SPC file has old format which is not supported.');
   }
   const le = (fversn === 0x4B); // little endian

   const fexper = view.getUint8(offset); offset += 1;
   const fexp = view.getInt8(offset); offset += 1;
   const fnpts = view.getUint32(offset, le); offset += 4;
   const ffirst = view.getFloat64(offset, le); offset += 8;
   const flast = view.getFloat64(offset, le); offset += 8;
   const fnsub = view.getUint32(offset, le); offset += 4;
   const fxtype = view.getUint8(offset); offset += 1;
   const fytype = view.getUint8(offset); offset += 1;
   const fztype = view.getUint8(offset); offset += 1;
   const fpost = view.getUint8(offset); offset += 1;
   const fdate = view.getUint32(offset, le); offset += 4;
   const fres = readString(view, 9, offset); offset += 9;
   const fsource = readString(view, 9, offset); offset += 9;
   const fpeakpt = view.getUint16(offset, le); offset += 2;
   const fspare = Array.from({ length: 8 }, () => {const v = view.getFloat32(offset, le); offset += 4; return v;});
   const fcmnt = readString(view, 130, offset); offset += 130;
   const fcatxt = readString(view, 30, offset); offset += 30;
   const flogoff = view.getUint32(offset, le); offset += 4;

   const [TSPREC, TCGRAM, TMULTI, TRANDM, TORDRD, TALABS, TXYXYS, TXVALS] = byteToBits(ftflgs);
   const xaxisName = fxtype === 255 ? xaxisNames[31] : xaxisNames[fxtype];
   const xaxisUnit = fxtype === 255 ? xaxisUnits[31] : xaxisUnits[fxtype];
   const yaxisName = fytype <= 26 ? yaxisNames[fytype] :
      (fytype >= 128 && fytype <= 131 ? yaxisNamesExt[fytype - 128] : 'Unknown');

   return {
      fversn,
      ftflgsv: ftflgs,
      ftflgs: {TSPREC, TCGRAM, TMULTI, TRANDM, TORDRD, TALABS, TXYXYS, TXVALS},
      fexper,
      fexp,
      fnpts,
      ffirst,
      flast,
      fnsub,
      fxtype,
      fytype,
      fztype,
      fpost,
      fdate,
      fres,
      fsource,
      fpeakpt,
      fspare,
      fcmnt,
      fcatxt,
      flogoff,
      xaxisName,
      xaxisUnit,
      yaxisName,
   };
}


/**
 * SPC file reader.
 * @param {Object} dataFile - File object (from form element).
 * @returns {Object} JSON with header, x and y-values, and object labels.
 */
async function readSPCFile(dataFile) {

   // check extention
   const fileExt = dataFile.name.slice(-3);
   if (fileExt.toLowerCase() !== 'spc') {
      throw new Error(`One of the selected files is not SPC (${dataFile.name}).`);
   }

   // get filename, read data to array buffer and add a view
   const fileName = dataFile.name.slice(0, -4);
   const buffer = await dataFile.arrayBuffer();
   const view = new DataView(buffer);

   // read and parse header
   let offset = 0;
   const hdr = parseSPCHeader(view, offset); offset += 512;
   const npts = hdr.fnpts;
   if (npts < 2 ) {
      throw new Error(`Error reading SPC file (${dataFile.name}).`);
   }

   // a file where every spectrum has x-values of its own is not read: the reader takes one set
   // of x-values for the file, and with TMULTI the header's point count of such a file is the
   // offset of a directory of the spectra, not a count
   if (hdr.ftflgs.TXYXYS) {
      throw new Error('SPC file has separate x-values for every spectrum, which is not supported ' +
         `(${dataFile.name}).`);
   }

   // the version byte gives the byte order of the data, as of the header
   const le = hdr.fversn === 0x4B;

   // process x-values
   let xvalues = new Array(npts);
   if (hdr.ftflgs.TXVALS) {
      // they are given explicitly
      for (let i = 0; i < npts; i++) {
         const v = view.getFloat32(offset, le); offset = offset + 4
         xvalues[i] = v;
      }
   } else {
      // generate them
      const dx = (hdr.flast - hdr.ffirst) / (npts - 1);
      for (let i = 0; i < npts; i++) {
         xvalues[i] = hdr.ffirst + i * dx;
      }
   }

   // process spectral values. the exponent in the header says how they are stored: 0x80 (-128 as
   // a signed byte) means 32-bit floats, any other value means integers to be scaled by
   // 2^(exponent - 32), or 16-bit integers scaled by 2^(exponent - 16) when the TSPREC flag is
   // set. with TMULTI every spectrum has its own exponent in its sub-header, unless the header
   // says float. the values used to be read as floats whatever the exponent
   const yvalues = new Float64Array(npts * hdr.fnsub);
   for (let s = 0; s < hdr.fnsub; s++) {
      const subexp = view.getInt8(offset + 1);
      const exp = hdr.fexp === -128 || !hdr.ftflgs.TMULTI ? hdr.fexp : subexp;
      offset = offset + 32;
      for (let i = 0; i < npts; i++) {
         if (exp === -128) {
            yvalues[s * npts + i] = view.getFloat32(offset, le);
            offset = offset + 4;
         } else if (hdr.ftflgs.TSPREC) {
            yvalues[s * npts + i] = view.getInt16(offset, le) * 2 ** (exp - 16);
            offset = offset + 2;
         } else {
            yvalues[s * npts + i] = view.getInt32(offset, le) * 2 ** (exp - 32);
            offset = offset + 4;
         }
      }
   }

   // generate object labels
   const objlabels  = [];
   if (hdr.fnsub > 1) {
      const nums = seq(1, hdr.fnsub);
      nums.forEach(v => { objlabels.push(fileName + '_' + v);});
   } else {
      objlabels.push(fileName)
   }

   return {hdr, xvalues, yvalues,  objlabels};
}


/**
 * SPC files reader.
 * @param {File[]} dataFiles - array of File objects. several files come from the file selector as
 * an array, from the dialog and a drop alike since svelte-controls-basic 3.2.0, while a single
 * one comes as the File itself and is wrapped by the caller; a FileList is accepted too.
 * @returns {Object} JSON with dataset, where spectra from individual files are combined into matrix.
 */
export async function readSPCFiles(dataFiles) {

   if (!(dataFiles instanceof FileList || Array.isArray(dataFiles))) {
      throw new Error('readSPCFiles: expected FileList or array of files.');
   }

   const nFiles = dataFiles.length;
   const spectra = [];
   let objlabels = [];
   let nSpectra = [];

   // read first file
   const first = await readSPCFile(dataFiles[0]);
   const nPoints = first.xvalues.length;
   spectra.push(first.yvalues);
   objlabels = objlabels.concat(first.objlabels);
   nSpectra.push(first.hdr.fnsub);

   // process the rest
   if (nFiles > 1) {
      for (let n = 1; n < nFiles; n++) {
         const out = await readSPCFile(dataFiles[n])
         if (out.xvalues.length !== nPoints) {
            throw new Error('Number of spectral values in SPC files is different.');
         }
         spectra.push(out.yvalues);
         objlabels = objlabels.concat(out.objlabels);
         nSpectra.push(out.hdr.fnsub);
      }
   }

   // merge all spectra together
   const nSpectraTot = nSpectra.reduce((acc, v) => acc + v, 0);

   const values = new Float64Array(nSpectraTot * nPoints);
   let offset = 0;
   for (let i = 0; i < spectra.length; i++) {
      values.set(spectra[i], offset);
      offset += nSpectra[i] * nPoints;
   }

   const valuesMat = new Matrix(values, nPoints, nSpectraTot);
   const varindices = seq(0, nPoints - 1);
   const objindices = seq(0, nSpectraTot - 1);
   const varlabels = first.xvalues.map(v => v.toFixed(1));

   return {
      values: valuesMat.t(),
      nresp: 0,
      header: 'values',
      hasLabels: true,
      hasClassNames: false,
      labelsVarname: '',
      classLabelsVarname: '',
      classes: [],
      classNames: [],
      objlabels,
      objindices,
      varlabels,
      varvalues: first.xvalues,
      varindices,
      varrev: first.xvalues[1] < first.xvalues[0],
      sep: ',',
      dec: '.',
      varvaluesName: first.hdr.xaxisName,
      varvaluesUnits: first.hdr.xaxisUnit,
      varAxisTitle: first.hdr.xaxisName + (first.hdr.xaxisUnit.length > 0 ? ', ' + first.hdr.xaxisUnit : ''),
      fileName: `${nFiles} SPC files`
   }

}


/**
 * Computes the 95% confidence interval of an expected rate (1 - alpha / 100) for a sample size
 * using the Normal distribution approximation.
 * @param {number} n - sample size.
 * @param {number} alpha - significance level in percent.
 * @returns {Array} the expected rate and the margin of the interval around it.
 */
export function getCI(n, alpha) {
   const cl = 1 - alpha / 100;
   const sigma = Math.sqrt(n * cl * (1 - cl));
   const em = 1.96 * sigma / n;
   return [cl, em];
}


/**
 * Takes dataset with class column and adds dummy response column for each class but the last
 * 'reduced' ones.
 * @param {Object} dataset - JSON with original dataset.
 * @param {number} reduced - classes left without a dummy column (pls1da 1, pls2da 0).
 * @param {number} other - value for rows outside the class (pls1da -1, pls2da 0).
 * @returns {Object} - JSON with new dataset.
 */
export function addDummy(dataset, reduced = 1, other = -1) {
   if (!dataset.classNames || dataset.classNames.length === 0) return dataset;

   const nresp = dataset.classNames.length - reduced;
   const npred = dataset.values.ncols;
   const nrows = dataset.values.nrows;
   let offset = nresp * nrows;

   const values = Matrix.zeros(nrows, npred + nresp);
   values.v.set(dataset.values.v, offset)

   for (let i = 0; i < nresp; i++) {
      offset = i * nrows;
      const className = dataset.classNames[i];
      for (let j = 0; j < nrows; j++) {
         values.v[offset + j] = dataset.classes[j] === className ? 1 : other;
      }
   }

   const varlabels = [...dataset.classNames.slice(0, nresp), ...dataset.varlabels];
   const varvalues = [...seq(1, nresp), ...dataset.varvalues];
   const varindices = [...seq(0, nresp - 1), ...dataset.varindices];

   return {

      values,
      nresp,
      varlabels,
      varvalues,
      varindices,

      header: dataset.header,
      hasLabels: dataset.hasLabels,
      hasClassNames: dataset.hasClassNames,
      labelsVarname: dataset.labelsVarname,
      classLabelsVarname: dataset.classLabelsVarname,
      objlabels: dataset.objlabels,
      objindices: dataset.objindices,
      varrev: dataset.varrev,
      classes: dataset.classes,
      classNames: dataset.classNames,
      sep: dataset.sep,
      dec: dataset.dec,
      varvaluesName: dataset.varvaluesName,
      varvaluesUnits: dataset.varvaluesUnits,
      varAxisTitle: dataset.varAxisTitle,
      fileName: dataset.fileName
   };
}

/**
 * Creates a copy (clone) of full data with its values replaced.
 *
 * Every field but 'values' is deep copied, so the clone shares nothing with 'd'; 'values' is
 * taken as given and not copied, because it is the new matrix the caller has just computed.
 * A caller holding the set as state snapshots the fields it hands in, because structuredClone
 * refuses a proxy, and leaves the matrix out: it is a class instance, never proxied, and a
 * snapshot of it is a full copy of the data that is dropped here.
 *
 * @param {Object} d - JSON with full data.
 * @param {Matrix} values - matrix with new (e.g. preprocessed) values, must have the same
 *    dimension as 'd.values'. It is required — cloning without replacing the values has no
 *    caller and throws.
 * @returns {Object} JSON with the cloned data.
 */
export function cloneFullData(d, values) {
   if (!values || values.nrows !== d.values.nrows || values.ncols !== d.values.ncols) {
      throw new Error('Object with values seems to be wrong for cloning.');
   }

   const { values: _oldValues, ...rest } = d;
   const out = structuredClone(rest);
   out.values = values;
   return out;
}


/**
 * Computes polygons for elements of bounded Voronoi diagram for given coordinate system.
 *
 * @param {TypedArray|Array} Z - centroids of the classes (nclasses x (nclasses - 1), column-major),
 *    as 'damodel_get' returns them.
 * @param {number} comp1 - column of U on the x-axis (from 1).
 * @param {number} comp2 - column of U on the y-axis (from 1).
 * @param {Array} xlim - x-axis limits
 * @param {Array} ylim - y-axis limits
 * @returns {Array} for each class an array with two vectors, the x- and y-coordinates of its
 *    cell within the limits (two empty vectors when nothing is left).
 */
export function boundedVoronoi(Z, comp1, comp2, xlim, ylim) {

   const N = nclassesOfZ(Z);
   const cx = Z.slice((comp1 - 1) * N, comp1 * N);
   const cy = Z.slice((comp2 - 1) * N, comp2 * N);
   const polygons = new Array(N);

   const xmin = xlim[0];
   const xmax = xlim[1];
   const ymin = ylim[0];
   const ymax = ylim[1];

   for (let i = 0; i < N; i++) {
      let poly = [
         xmin, ymin,
         xmax, ymin,
         xmax, ymax,
         xmin, ymax
      ];

      const xi = cx[i];
      const yi = cy[i];

      for (let j = 0; j < N; j++) {
         if (j === i) continue;

         const xj = cx[j];
         const yj = cy[j];

         const a = xj - xi;
         const b = yj - yi;

         // coincident centers
         if (a === 0 && b === 0) continue;
         const c = 0.5 * (xj * xj + yj * yj - xi * xi - yi * yi);
         poly = clipHalfPlane(poly, a, b, c);
         if (poly.length === 0) break;
      }

      const nv = poly.length / 2;
      const x = new Float64Array(nv);
      const y = new Float64Array(nv);

      for (let k = 0; k < nv; k++) {
         x[k] = poly[2 * k];
         y[k] = poly[2 * k + 1];
      }

      polygons[i] = [ new Vector(x), new Vector(y) ];
   }

   return polygons;
}


/**
 * Clips a polygon against the half-plane a*x + b*y <= c.
 * Returns the portion of the polygon lying on the allowed side of the boundary line.
 *
 * @param {Array} poly - coordinates of the polygon, flat: [x1, y1, x2, y2, ...]
 * @param {Number} a - parameter of the half plane
 * @param {Number} b - parameter of the half plane
 * @param {Number} c - parameter of the half plane
 * @returns {Array} the clipped polygon, flat as 'poly', empty when nothing is left.
 */
function clipHalfPlane(poly, a, b, c) {
   const n = poly.length / 2;
   if (n === 0) return [];

   const out = [];

   const eps =
      1e-12 * (1 + Math.abs(a) + Math.abs(b) + Math.abs(c));

   for (let k = 0; k < n; k++) {
      const k2 = (k + 1) % n;

      const x1 = poly[2 * k];
      const y1 = poly[2 * k + 1];

      const x2 = poly[2 * k2];
      const y2 = poly[2 * k2 + 1];

      const f1 = a * x1 + b * y1 - c;
      const f2 = a * x2 + b * y2 - c;

      const in1 = f1 <= eps;
      const in2 = f2 <= eps;

      if (in1 && in2) {
         out.push(x2, y2);

      } else if (in1 && !in2) {
         const t = f1 / (f1 - f2);

         out.push(
               x1 + t * (x2 - x1),
               y1 + t * (y2 - y1)
         );

      } else if (!in1 && in2) {
         const t = f1 / (f1 - f2);

         out.push(
               x1 + t * (x2 - x1),
               y1 + t * (y2 - y1),
               x2,
               y2
         );
      }
   }

   return out;
}


/**
 * Computes the acceptance ellipses of soft PLS-DA for a pair of U columns.
 *
 * The boundary of class k is d = d0 / Nd * qchisq(1 - alpha, Nd) (eq. 13). For more than three
 * classes U has more than two columns and the ellipse is the projection (shadow) of the class
 * ellipsoid, so a point outside it is rejected by the class, a point inside may be rejected too.
 * A robust Sk^-1 can have a zero weight, the region is then unbounded along that direction: it
 * is drawn as a strip, cut at the axis limits.
 *
 * @param {TypedArray|Array} Z - centroids of the classes (nclasses x (nclasses - 1), column-major),
 *    as 'damodel_get' returns them.
 * @param {TypedArray} Sinv - inverse covariance matrices of the classes, one
 *    (nclasses - 1) x (nclasses - 1) block after another, as 'damodel_get' returns them.
 * @param {TypedArray|Array} d0 - d0 of each class.
 * @param {TypedArray|Array} Nd - Nd of each class.
 * @param {number|Array<number>} p - probability of the chi-square quantile, one for all classes
 *    or a plain array with one per class (1 - alpha, or (1 - gamma)^(1 / nobj) for outliers).
 * @param {number} comp1 - column of U on the x-axis (from 1).
 * @param {number} comp2 - column of U on the y-axis (from 1).
 * @param {Array} xlim - x-axis limits.
 * @param {Array} ylim - y-axis limits.
 * @param {number} [npoints=100] - number of segments of each ellipse.
 * @returns {Array} for each class an array with two vectors, the x- and y-coordinates of the
 *    closed ellipse, or of the strip polygon within the limits.
 */
export function plsdaEllipse(Z, Sinv, d0, Nd, p, comp1, comp2, xlim, ylim, npoints = 100) {

   const N = nclassesOfZ(Z);
   const m = N - 1;
   const mm = m * m;
   const i1 = comp1 - 1;
   const i2 = comp2 - 1;

   const cx = Z.slice(i1 * N, (i1 + 1) * N);
   const cy = Z.slice(i2 * N, (i2 + 1) * N);

   const xmid = (xlim[0] + xlim[1]) / 2;
   const ymid = (ylim[0] + ylim[1]) / 2;
   const diag = Math.hypot(xlim[1] - xlim[0], ylim[1] - ylim[0]);

   const cost = new Float64Array(npoints + 1);
   const sint = new Float64Array(npoints + 1);
   for (let t = 0; t <= npoints; t++) {
      const theta = 2 * Math.PI * t / npoints;
      cost[t] = Math.cos(theta);
      sint[t] = Math.sin(theta);
   }

   if (!Array.isArray(p)) p = Array.from({length: N}, () => p);
   const ellipses = new Array(N);
   for (let k = 0; k < N; k++) {

      const r2 = d0[k] / Nd[k] * qchisq(p[k], Nd[k]);
      const r = Math.sqrt(r2);

      // the 2 x 2 block of the covariance matrix Sk, not of its inverse, gives the projection.
      // Sk is built from the eigenvectors and weights of Sk^-1; a weight below 'wmin' (a zero
      // robust weight) gets 'wmin', which stretches the ellipse 1e3 times past the plot
      const far = 1e3 * (diag + Math.hypot(cx[k] - xmid, cy[k] - ymid));
      const wmin = r2 / (far * far);
      const [w, V] = symEigen(Sinv.subarray(k * mm, (k + 1) * mm), m);

      // the block is F F', column j of F is (V[i1, j], V[i2, j]) / sqrt(w[j])
      let strip = false;
      const fx = new Float64Array(m);
      const fy = new Float64Array(m);
      for (let j = 0; j < m; j++) {
         let wj = w[j];
         if (wj < wmin) {
            wj = wmin;
            strip = true;
         }
         fx[j] = V[j * m + i1] / Math.sqrt(wj);
         fy[j] = V[j * m + i2] / Math.sqrt(wj);
      }

      // Cholesky factor of the block, each point is c + r * L * (cos, sin). its determinant is
      // the sum of squared 2 x 2 minors of F (Cauchy-Binet): sxx * syy - sxy^2 cancels when a
      // weight is stretched, and the strip came out too wide
      let sxx = 0, sxy = 0, det = 0;
      for (let j = 0; j < m; j++) {
         sxx += fx[j] * fx[j];
         sxy += fx[j] * fy[j];
         for (let i = 0; i < j; i++) {
            const minor = fx[i] * fy[j] - fx[j] * fy[i];
            det += minor * minor;
         }
      }
      const l11 = Math.sqrt(sxx);
      const l21 = sxy / l11;
      const l22 = Math.sqrt(det / sxx);

      let poly = new Array(2 * (npoints + 1));
      for (let t = 0; t <= npoints; t++) {
         poly[2 * t] = cx[k] + r * l11 * cost[t];
         poly[2 * t + 1] = cy[k] + r * (l21 * cost[t] + l22 * sint[t]);
      }

      if (strip) {
         poly = clipHalfPlane(poly, 1, 0, xlim[1]);
         poly = clipHalfPlane(poly, -1, 0, -xlim[0]);
         poly = clipHalfPlane(poly, 0, 1, ylim[1]);
         poly = clipHalfPlane(poly, 0, -1, -ylim[0]);
      }

      const nv = poly.length / 2;
      const x = new Float64Array(nv);
      const y = new Float64Array(nv);
      for (let t = 0; t < nv; t++) {
         x[t] = poly[2 * t];
         y[t] = poly[2 * t + 1];
      }

      ellipses[k] = [ new Vector(x), new Vector(y) ];
   }

   return ellipses;
}


/**
 * Computes the outlier circles of hard PLS-DA for a pair of U columns.
 *
 * A member of class k is an outlier when its squared Euclidean distance to the centroid exceeds
 * d0 / Nd * qchisq((1 - gamma)^(1 / nobj), Nd) (eq. 10). The region is a sphere, so its
 * projection on any pair of columns is a circle with the same radius.
 *
 * @param {TypedArray|Array} Z - centroids of the classes (nclasses x (nclasses - 1), column-major),
 *    as 'damodel_get' returns them.
 * @param {TypedArray|Array} d0 - d0 of each class ('hd0').
 * @param {TypedArray|Array} Nd - Nd of each class ('hNd').
 * @param {Array<number>} p - probability of the chi-square quantile of each class,
 *    (1 - gamma)^(1 / nobj).
 * @param {number} comp1 - column of U on the x-axis (from 1).
 * @param {number} comp2 - column of U on the y-axis (from 1).
 * @param {number} [npoints=100] - number of segments of each circle.
 * @returns {Array} for each class an array with two vectors, the x- and y-coordinates of the
 *    closed circle.
 */
export function plsdaCircle(Z, d0, Nd, p, comp1, comp2, npoints = 100) {

   const N = nclassesOfZ(Z);
   const cx = Z.slice((comp1 - 1) * N, comp1 * N);
   const cy = Z.slice((comp2 - 1) * N, comp2 * N);

   const circles = new Array(N);
   for (let k = 0; k < N; k++) {
      const r = Math.sqrt(d0[k] / Nd[k] * qchisq(p[k], Nd[k]));
      const x = new Float64Array(npoints + 1);
      const y = new Float64Array(npoints + 1);
      for (let t = 0; t <= npoints; t++) {
         const theta = 2 * Math.PI * t / npoints;
         x[t] = cx[k] + r * Math.cos(theta);
         y[t] = cy[k] + r * Math.sin(theta);
      }

      circles[k] = [ new Vector(x), new Vector(y) ];
   }

   return circles;
}


/**
 * Computes eigenvalues and eigenvectors of a small symmetric matrix (cyclic Jacobi rotations).
 *
 * @param {TypedArray} A - values of the m x m symmetric matrix (column-major).
 * @param {number} m - number of rows and columns.
 * @returns {Array} eigenvalues (Float64Array, m) and eigenvectors (Float64Array, m x m,
 *    column-major, column j for eigenvalue j).
 */
function symEigen(A, m) {
   const a = Float64Array.from(A);
   const v = new Float64Array(m * m);
   for (let i = 0; i < m; i++) v[i * m + i] = 1;

   let norm2 = 0;
   for (let i = 0; i < m * m; i++) norm2 += a[i] * a[i];

   for (let sweep = 0; sweep < 100; sweep++) {
      let off = 0;
      for (let q = 1; q < m; q++) {
         for (let p = 0; p < q; p++) off += a[q * m + p] * a[q * m + p];
      }
      if (off <= 1e-32 * norm2) break;

      for (let p = 0; p < m - 1; p++) {
         for (let q = p + 1; q < m; q++) {
            const apq = a[q * m + p];
            if (apq === 0) continue;

            // rotation which zeroes a[p, q]: A = J' A J, V = V J
            const theta = (a[q * m + q] - a[p * m + p]) / (2 * apq);
            const t = (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
            const c = 1 / Math.sqrt(t * t + 1);
            const s = t * c;

            for (let k = 0; k < m; k++) {
               const akp = a[p * m + k], akq = a[q * m + k];
               a[p * m + k] = c * akp - s * akq;
               a[q * m + k] = s * akp + c * akq;
            }
            for (let k = 0; k < m; k++) {
               const apk = a[k * m + p], aqk = a[k * m + q];
               a[k * m + p] = c * apk - s * aqk;
               a[k * m + q] = s * apk + c * aqk;
            }
            for (let k = 0; k < m; k++) {
               const vkp = v[p * m + k], vkq = v[q * m + k];
               v[p * m + k] = c * vkp - s * vkq;
               v[q * m + k] = s * vkp + c * vkq;
            }
         }
      }
   }

   const values = new Float64Array(m);
   for (let i = 0; i < m; i++) values[i] = a[i * m + i];
   return [values, v];
}


/**
 * Returns the number of classes of a PLS-DA model from its centroids, which have
 * nclasses x (nclasses - 1) values.
 *
 * @param {TypedArray|Array} Z - centroids of the classes.
 * @returns {number} number of classes.
 */
function nclassesOfZ(Z) {
   const N = (1 + Math.sqrt(1 + 4 * Z.length)) / 2;
   if (!Number.isInteger(N) || N < 2) {
      throw new Error('Centroids "Z" must have nclasses x (nclasses - 1) values.');
   }
   return N;
}
